// src/screens/EventDetailScreen.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Modal, Pressable,
  Share, Platform, Image, TextInput
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useNavigation } from '@react-navigation/native';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import FounderBadge from '../components/FounderBadge';

import { useError } from '../components/ErrorContext';

import { db } from '../../firebaseConfig';
import {
  doc, onSnapshot, collection, getDoc,
  query, where, limit, getDocs
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { PAYOUT_STRUCTURES, structureText, structureName } from '../constants/payoutStructures';
import { GAME_PROFILES, GT7_LOBBY_SETTINGS, GT7_SETTING_SECTION_LABELS, GT7_SETTING_KEY_LABELS, extractLobbyValues, MADDEN_GAME_SETTINGS, maddenSettingsFromLegacyFormat } from '../constants/gameData';
import { resolveGameProfile } from '../constants/communityGames';
import { buildHostRewardTable, getHostTier, calculateMatchHostPay, calculateHostEventEarnings, computeHostRoundReward, HOST_CRIT_REWARD_CONFIG } from '../constants/hostRewards';
import {
  getFightingBracketMeta,
  getMobaBracketMeta,
  getRtsBracketMeta,
  getShooterBracketMeta,
  getSportsBracketMeta,
} from '../constants/gameMeta';
import { useEventEligibility } from '../hooks/useEventEligibility';

import { useAdminAuth } from '../hooks/useAdminAuth';
import usePayouts from '../hooks/usePayouts';
import { Ionicons } from '@expo/vector-icons';

import {
  COLORS, toCents, fmtUSD, fmtDate, toDateLike,
  hasPaidEntry, ordinal,
} from '../utils/eventHelper';
import { fmtCrits } from '../utils/currencyHelper';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { openTermsOfService } from '../utils/openTermsOfService';

/* ---------- Countdown ---------- */
function useCountdown(untilDate) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const target = untilDate?.getTime?.() ?? null;
  const ms = Math.max(0, (target ?? 0) - now);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { done: !target || ms <= 0, d, h, m, sec };
}

// Pretty USD formatter
const usd = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n / 100);
};



// Warzone battle-royale display labels: in-game squad size -> name.
const SQUAD_SIZE_LABELS = { 1: 'Solos', 2: 'Duos', 3: 'Trios', 4: 'Quads' };

// Static Warzone BR payout structures - LOCAL mirror of the wz-* entries in
// functions/constants/payoutStructures.js (the frontend PAYOUT_STRUCTURES
// constants file doesn't carry them yet; PAYOUT_STRUCTURES is checked first
// so a future sync wins). BR payouts do NOT scale with entrant count the way
// GT7 showdown bands do - these bands are always the payout shape.
// plainDescription = the Crits-OFF text (structureText), same copy as the
// constants file's wz-* entries.
const WZ_PAYOUT_STRUCTURES = {
  'wz-solos-top-10': {
    name: 'Battle Royale Top 10',
    description: 'Placement payouts - the top 10 finishers each earn a share of the Crit pool.',
    plainDescription: 'Drop in and outlast the lobby. Final placement decides the standings: the deeper you survive, the higher you finish.',
    gridSize: 50,
    totalRounds: 1,
    bands: [
      { tier: 'Champion', minRank: 1, maxRank: 1, percent: 30 },
      { tier: '2nd Place', minRank: 2, maxRank: 2, percent: 20 },
      { tier: '3rd Place', minRank: 3, maxRank: 3, percent: 14 },
      { tier: '4th Place', minRank: 4, maxRank: 4, percent: 10 },
      { tier: '5th Place', minRank: 5, maxRank: 5, percent: 8 },
      { tier: '6th Place', minRank: 6, maxRank: 6, percent: 6 },
      { tier: '7th Place', minRank: 7, maxRank: 7, percent: 4 },
      { tier: '8th Place', minRank: 8, maxRank: 8, percent: 3 },
      { tier: '9th Place', minRank: 9, maxRank: 9, percent: 3 },
      { tier: '10th Place', minRank: 10, maxRank: 10, percent: 2 },
    ],
  },
  'wz-squads-top-5': {
    name: 'Battle Royale Squads Top 5',
    description: 'Placement payouts - the top 5 squads share the Crit pool; a squad\'s share splits evenly among its members.',
    plainDescription: 'Squad up and fight to the final circle. Squads are ranked by final placement, and every member shares their squad\'s placement.',
    gridSize: 50,
    totalRounds: 1,
    perSquad: true,
    bands: [
      { tier: 'Champion Squad', minRank: 1, maxRank: 1, percent: 40 },
      { tier: '2nd Squad', minRank: 2, maxRank: 2, percent: 25 },
      { tier: '3rd Squad', minRank: 3, maxRank: 3, percent: 15 },
      { tier: '4th Squad', minRank: 4, maxRank: 4, percent: 12 },
      { tier: '5th Squad', minRank: 5, maxRank: 5, percent: 8 },
    ],
  },
};

// Local resolver for bracket marketing/title metadata
function resolveBracketMeta({ gameKey, bracketSize, type }) {
  if (!gameKey || !bracketSize) return null;
  if (type === 'fighting') return getFightingBracketMeta(gameKey, bracketSize);
  if (type === 'moba')     return getMobaBracketMeta(gameKey, bracketSize);
  if (type === 'rts')      return getRtsBracketMeta(gameKey, bracketSize);
  if (type === 'shooter')  return getShooterBracketMeta(gameKey, bracketSize);
  if (type === 'sports')   return getSportsBracketMeta(gameKey, bracketSize);
  return null;
}

// ============================================================================
// Host Earnings Preview Component (Phase 3.5)
// 
// Dual "Full Field" / "Current" columns, personalized to the viewer's tier.
// Shows per-round base earnings, completion bonus estimate, projected total.
// Hidden for H2H, hidden for participants (they can't switch to host).
// Non-hosts see a Tier 1 preview as incentive.
// ============================================================================

function getRoundName(roundNumber, totalRounds) {
  if (roundNumber === totalRounds) return 'Finals';
  if (roundNumber === totalRounds - 1) return 'Semis';
  if (roundNumber === totalRounds - 2) return 'Quarters';
  return `R${roundNumber}`;
}

// Bracket size in TEAMS (not players). Team events store bracketSize = teams;
// maxEntrants/maxPlayers are total individuals, so divide by teamSize.
function bracketTeams(ev) {
  const t = Number(ev?.bracketSize || ev?.maxTeams || 0);
  if (t >= 2) return t;
  const players = Number(ev?.maxEntrants || ev?.maxPlayers || 2);
  return Math.max(2, Math.floor(players / (Number(ev?.teamSize) || 1)));
}

// One host per MATCH per round. Bracket: round r has bracketSize/2^r matches
// (16 teams → R1:8, R2:4, Semis:2, Finals:1). Heats (GT7/iRacing racing): round
// r has heatsCount/2^(r-1) (R1 = every heat). Mirrors events.js / hosts.js.
function matchesInRound(ev, r) {
  const heats = Number(ev?.heatsCount || ev?.heatCount || 0);
  const mode = String(ev?.mode || '').toLowerCase();
  if ((mode === 'racing' || mode === 'showdown') && heats >= 2) {
    return Math.max(1, Math.ceil(heats / Math.pow(2, r - 1)));
  }
  // Single-lobby showdowns (GT7 one-race, Warzone BR): one match, one host
  // slot - never the bracket halving math (a 50-entrant BR doc would
  // otherwise read as 25 phantom R1 matches).
  if (mode === 'showdown') return 1;
  return Math.max(1, Math.floor(bracketTeams(ev) / Math.pow(2, r)));
}

// critsEnabled defaults TRUE so an unpassed prop can never hide the card in
// ON mode; the screen passes the live flag (evergreen 2.3: OFF skips the host
// Crit mint server-side, so the Crit-reward promise is hidden while hosting
// itself still counts).
const HostEarningsPreview = ({ event, navigation, myHostEntry, myEntry, myHostProfile, critsEnabled = true }) => {
  if (!event) return null;

  // H2H HARDENING (Layer 1): H2H matches never have host rewards
  const eventMode = String(event.mode || event.format || event.type || '').toLowerCase();
  if (eventMode === 'h2h') return null;

  // Participants can't switch to host — don't show. Exception: a racing host
  // (hostRaces) holds an entry AND hosts — keep their earnings card visible.
  const myEntryUid = myEntry ? (myEntry.userId || myEntry.uid || myEntry.id) : null;
  const viewerIsRacingHost = event.hostRaces === true && !!myEntryUid
    && (event.hostUid === myEntryUid || event.hostUserId === myEntryUid);
  if (myEntry && !viewerIsRacingHost) return null;

  // Host reward (founder 2026-07-16): hosts earn MINTED Crits per round = 20% of
  // the lobby pot (players in the match x entry), floor 1 / cap 25, with a depth
  // multiplier (up to 2x) that grows the more rounds you host in the event.
  // Project the FIRST round (ordinal 1) for this specific event so the host sees
  // a real number; note it climbs with depth. Mirrors computeHostRoundReward.
  const isRegisteredHost = !!myHostEntry;
  const pctLabel = `${Math.round(HOST_CRIT_REWARD_CONFIG.potPct * 100)}%`;

  const entryCrits = Number(event.critEntryCost) || 0;
  const isRacing = eventMode === 'racing' || eventMode === 'showdown';
  const matchPlayers = isRacing
    ? (Number(event.gridSize) || Number(event.maxPlayers) || 0)
    : Math.max(2, 2 * (Number(event.teamSize) || 1));
  const firstRound = computeHostRoundReward({ entryCrits, playersInMatch: matchPlayers, ordinal: 1 });

  return (
    <View style={hostEarningsStyles.container}>
      <View style={hostEarningsStyles.header}>
        <Ionicons name="shield-checkmark" size={18} color="#FCD000" />
        <Text style={hostEarningsStyles.title}>{critsEnabled ? 'HOST REWARD' : 'HOSTING'}</Text>
      </View>

      {critsEnabled ? (
      <View style={{ backgroundColor: '#111', borderRadius: 8, padding: 12 }}>
        <Text style={{ color: '#999', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {pctLabel} of the lobby's Crit pool
        </Text>
        <Text style={{ color: '#FCD000', fontSize: 22, fontWeight: 'bold', marginTop: 2 }}>
          ~{firstRound.crits} Crit{firstRound.crits === 1 ? '' : 's'} / round
        </Text>
        <Text style={{ color: '#7d7d7d', fontSize: 11, marginTop: 4 }}>
          Round 1 estimate. Climbs up to 2x the more rounds you host.
        </Text>
      </View>
      ) : (
      <View style={{ backgroundColor: '#111', borderRadius: 8, padding: 12 }}>
        <Text style={{ color: '#7d7d7d', fontSize: 11 }}>
          Every round you host goes on your hosting record.
        </Text>
      </View>
      )}

      {!isRegisteredHost && (
        <Text style={hostEarningsStyles.link}>
          {critsEnabled ? 'Enter as Host to start earning Crits' : 'Enter as Host to run this lobby'}
        </Text>
      )}
    </View>
  );
};

// Styles for Host Earnings Preview (Phase 3.5 — dual column)
const hostEarningsStyles = StyleSheet.create({
  container: {
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#333',
    padding: 14,
    marginVertical: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  title: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 0.3,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  colHeader: {
    color: '#888',
    fontSize: 10,
    fontWeight: '700',
    textAlign: 'right',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  roundLabel: {
    color: '#ccc',
    fontSize: 12,
    fontWeight: '600',
  },
  label: {
    color: '#8a8a8a',
    fontSize: 12,
  },
  value: {
    color: '#ddd',
    fontSize: 12,
    textAlign: 'right',
  },
  highlightValue: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  link: {
    color: '#FCD000',
    fontSize: 12,
    marginTop: 10,
    textAlign: 'center',
    fontWeight: '600',
  },
});

// ============================================================================
// COORDINATOR RESULTS (founder 2026-08-12): on manual-results events the
// event creator records winners for live matches they are NOT playing in.
// Players' own reports still settle matches on agreement; this panel is the
// coordinator's direct line. Backend: submitMatchResult({ eventId, matchId,
// winnerUid }) — audited, resolves via the standard completion pipeline,
// disputes stay open as recourse.
// ============================================================================
function CoordinatorResultsPanel({ eventId, ev, uid, showError }) {
  const [matches, setMatches] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null); // `${matchId}:${winnerKey}`

  const mode = String(ev?.mode || '').toLowerCase();
  const enabled = !!uid && ev?.createdByUid === uid
    && ev?.manualResults === true
    && (mode === 'h2h' || mode === 'bracket')
    && ['seeded', 'locked', 'live'].includes(String(ev?.status || '').toLowerCase());

  useEffect(() => {
    if (!enabled || !eventId) { setMatches([]); return undefined; }
    const qy = query(
      collection(db, 'tournaments', eventId, 'matches'),
      where('status', '==', 'live'),
      limit(30),
    );
    const unsub = onSnapshot(qy, (snap) => {
      setMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setMatches([]));
    return () => unsub();
  }, [enabled, eventId]);

  if (!enabled) return null;

  const sideHasUid = (side, u) => [
    side?.userId, side?.captainUid,
    ...(Array.isArray(side?.rosterUids) ? side.rosterUids : []),
    ...(Array.isArray(side?.roster) ? side.roster : []),
  ].includes(u);
  // The coordinator records only matches they are NOT playing in — their own
  // matches go through the regular both-report flow (server enforces too).
  const rows = matches.filter((m) => m?.p1 && m?.p2 && !m.winnerId
    && !sideHasUid(m.p1, uid) && !sideHasUid(m.p2, uid));
  if (rows.length === 0) return null;

  const sideName = (side) => side?.teamName || side?.ign || side?.displayName || 'Player';
  const sideKey = (side) => side?.teamId || side?.pickupGroupId || side?.userId || side?.captainUid || null;

  const record = async (matchId, winnerUid) => {
    setBusyId(matchId);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'submitMatchResult')({ eventId, matchId, winnerUid });
    } catch (err) {
      const msg = String(err?.message || 'Could not record the result.');
      if (typeof showError === 'function') showError('Could not record result', msg);
    } finally {
      setBusyId(null);
      setConfirmKey(null);
    }
  };

  const winnerBtn = (m, side) => {
    const key = `${m.id}:${sideKey(side)}`;
    const armed = confirmKey === key;
    return (
      <TouchableOpacity
        key={key}
        disabled={busyId === m.id || !sideKey(side)}
        onPress={() => (armed ? record(m.id, sideKey(side)) : setConfirmKey(key))}
        style={{
          flex: 1, borderRadius: 8, borderWidth: 2, paddingVertical: 10,
          alignItems: 'center', marginTop: 8,
          borderColor: armed ? '#FCD000' : '#2a2a2a',
          backgroundColor: armed ? '#1a1604' : '#161616',
          opacity: busyId === m.id ? 0.5 : 1,
        }}
      >
        <Text style={{ color: armed ? '#FCD000' : '#EDEDED', fontWeight: '800', fontSize: 13 }} numberOfLines={1}>
          {armed ? `Confirm: ${sideName(side)} won` : `${sideName(side)} won`}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>RECORD RESULTS</Text>
      <Text style={{ color: '#999', fontSize: 12.5, lineHeight: 18, marginBottom: 4 }}>
        You coordinate this event. When a match wraps, record the winner — tap once
        to select, tap again to confirm. Players can dispute a posted result.
      </Text>
      {rows.map((m) => (
        <View key={m.id} style={{ borderTopColor: '#1e1e1e', borderTopWidth: 1, paddingTop: 10, marginTop: 10 }}>
          <Text style={{ color: '#ccc', fontSize: 13, fontWeight: '700' }} numberOfLines={1}>
            {`R${m.round || 1} - ${sideName(m.p1)} vs ${sideName(m.p2)}`}
          </Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {winnerBtn(m, m.p1)}
            {winnerBtn(m, m.p2)}
          </View>
        </View>
      ))}
    </View>
  );
}

export default function EventDetailScreen({ route }) {
  const { showError } = useError();
  const { user, isAdmin } = useAdminAuth();
  const navigation = useNavigation();
  // Evergreen step 2.3: settings/economy.critsEnabled — OFF hides cost,
  // balance and pool displays; the ENTER flow itself is unchanged.
  const critsEnabled = useCritsEnabled();

  const p = route?.params || {};
  const id = p.id || p.eventId || p.tournamentId || p.matchId;
  const coll = (p.collection || p.coll || p.kind || 'tournaments').toLowerCase();

  const [ev, setEv] = useState();
  const [entries, setEntries] = useState([]);
  const [profiles, setProfiles] = useState({});
  // Per-entrant badge + photo for the entrants list (Founder aura).
  const [entMeta, setEntMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [cancellingEvent, setCancellingEvent] = useState(false);
  const [runningItBack, setRunningItBack] = useState(false);
  const [agree, setAgree] = useState(false);

  const [requiredTitle, setRequiredTitle] = useState(null);

  const [payoutsOpen, setPayoutsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [calc, setCalc] = useState({ stage: null, key: null });

  // Team data (for display purposes only, not required for entry)
  const [myTeam, setMyTeam] = useState(null);
  
  // User balance state for payment info display
  const [balanceCents, setBalanceCents] = useState(0);
  const [giftBalanceCents, setGiftBalanceCents] = useState(0);
  // Crit balance (post-pivot play currency) for Crit-entry events
  const [critBalance, setCritBalance] = useState(0);
  
  // Entry confirmation modal
  const [showEntryConfirmModal, setShowEntryConfirmModal] = useState(false);
  
  // Host entry state (Phase 3: dual Participant/Host entry)
  const [myHostEntry, setMyHostEntry] = useState(null);
  const [showHostRoundModal, setShowHostRoundModal] = useState(false);
  const [hostSelectedRounds, setHostSelectedRounds] = useState([]);
  const [joiningAsHost, setJoiningAsHost] = useState(false);
  const [leavingAsHost, setLeavingAsHost] = useState(false);
  const [showHostTosModal, setShowHostTosModal] = useState(false);
  const [hostTosAccepted, setHostTosAccepted] = useState(false);
  // Racer-host opt-in (hosted-racer heats): standing for the whole event.
  const [racerHostOptIn, setRacerHostOptIn] = useState(false);
  const [raceSettingsOpen, setRaceSettingsOpen] = useState(false);
  // Madden game-settings editor (creator/admin, while event is scheduled)
  const [gameSettingsOpen, setGameSettingsOpen] = useState(false);
  const [editedGameSettings, setEditedGameSettings] = useState(null);
  const [savingGameSettings, setSavingGameSettings] = useState(false);
  const [hostEntryCount, setHostEntryCount] = useState(0);
  const [hostEntries, setHostEntries] = useState([]);
  const [myHostProfile, setMyHostProfile] = useState(null); // Global hosts/{uid} doc
  // Warzone BR (event-level single lobby): host claim + game-code submission.
  // The event doc IS the match doc (matchId === eventId).
  const [claimingHostSlot, setClaimingHostSlot] = useState(false);
  const [gameCodeInput, setGameCodeInput] = useState('');
  const [submittingGameCode, setSubmittingGameCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Check if user is eligible to join events (has IGN + stream key).
  // Platform-locked events (platform !== Cross-Platform) require an IGN
  // declared for the event's platform, not just any IGN for the game.
  const gameId = ev?.game;
  const eventPlatform = ev?.platform || null;
  const isPlatformLocked = !!eventPlatform && String(eventPlatform).toLowerCase() !== 'cross-platform';
  const {
    isEligible,
    isLoading: eligibilityLoading,
    missingRequirements,
    hasIGN,
    hasAnyIGN,
    ign: userIGN,
    hasStreamKey,
    serviceAccountName,
    serviceShortName,
  } = useEventEligibility(user?.uid, gameId, eventPlatform);

  /* ---------- Data subscriptions ---------- */
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    const unsub = onSnapshot(
      doc(db, coll, id),
      (snap) => { setEv(snap.exists() ? ({ id: snap.id, ...snap.data() }) : null); setLoading(false); },
      () => { setEv(null); setLoading(false); }
    );
    return () => unsub();
  }, [id, coll]);

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(collection(db, coll, id, 'entries'), (snap) =>
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [id, coll]);

  // Host entry subscription — track if current user is a host for this event
  useEffect(() => {
    if (!id || !user?.uid || coll !== 'tournaments') return;
    const unsub = onSnapshot(
      doc(db, coll, id, 'hostEntries', user.uid),
      (snap) => setMyHostEntry(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setMyHostEntry(null)
    );
    return () => unsub();
  }, [id, coll, user?.uid]);

  // Host entries subscription (count + full docs for host list)
  useEffect(() => {
    if (!id || coll !== 'tournaments') return;
    const unsub = onSnapshot(
      collection(db, coll, id, 'hostEntries'),
      (snap) => {
        setHostEntryCount(snap.size);
        setHostEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      () => { setHostEntryCount(0); setHostEntries([]); }
    );
    return () => unsub();
  }, [id, coll]);

  // Heat matches subscription — multi-heat racing events (mode 'racing',
  // heatsCount >= 2) render a HEATS card: which heat you're in, per-heat
  // status/host/room code, and needs-host claim buttons.
  const [heatMatches, setHeatMatches] = useState([]);
  const [claimingHeatId, setClaimingHeatId] = useState(null);
  const evIsHeatsEvent = String(ev?.mode || '').toLowerCase() === 'racing'
    && Math.max(
      Math.floor(Number(ev?.heatsCount || ev?.heatCount) || 0),
      Math.floor(Number(ev?.declaredHeatsCount) || 0)) >= 2;
  useEffect(() => {
    if (!id || coll !== 'tournaments' || !evIsHeatsEvent) { setHeatMatches([]); return; }
    const unsub = onSnapshot(
      collection(db, coll, id, 'matches'),
      (snap) => {
        setHeatMatches(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((m) => m.matchType === 'heat'
            || (Array.isArray(m.players) && m.players.length >= 2 && !m.p1 && !m.p2))
          .sort((a, b) => (a.round || 0) - (b.round || 0) || (a.matchNumber || 0) - (b.matchNumber || 0)));
      },
      () => setHeatMatches([]),
    );
    return () => unsub();
  }, [id, coll, evIsHeatsEvent]);

  const onClaimHeat = useCallback(async (matchId) => {
    if (!user?.uid || !ev?.id || !matchId) return;
    try {
      setClaimingHeatId(matchId);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'claimUnhostedMatch')({
        eventId: ev.id,
        matchId,
        hostTosAccepted: true,
      });
      showError('Heat claimed', 'You host this heat. Create the private lobby in GT7, then submit the 6-character Room ID.');
    } catch (e) {
      const msg = String(e?.message || e);
      showError('Could not claim heat', msg.replace(/^.*?:\s*/, '') || 'Try again.');
    } finally {
      setClaimingHeatId(null);
    }
  }, [user?.uid, ev?.id, showError]);

  // Global host profile — for earnings preview tier display before entering
  useEffect(() => {
    if (!user?.uid) { setMyHostProfile(null); return; }
    const unsub = onSnapshot(
      doc(db, 'hosts', user.uid),
      (snap) => setMyHostProfile(snap.exists() ? snap.data() : null),
      () => setMyHostProfile(null)
    );
    return () => unsub();
  }, [user?.uid]);

  // Fetch user balances for payment info
  useEffect(() => {
    if (!user?.uid) return;
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setBalanceCents(data.balanceCents || 0);
          setGiftBalanceCents(data.giftBalanceCents || 0);
          setCritBalance(data.critBalance || 0);
        }
      },
      (err) => console.warn('Balance fetch error:', err)
    );
    return () => unsubscribe();
  }, [user?.uid]);
  
  // Combined balance for display
  const totalBalanceCents = balanceCents + giftBalanceCents;

  // Hydrate display names for entrants + hosts
  useEffect(() => {
    if (!entries?.length && !hostEntries?.length) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      const metaUpdates = {};
      const uniqueUids = new Set();
      // Any name cached on the entry/host doc — the fallback when a profile
      // lookup fails. NEVER fall back to the raw uid (account id must not
      // surface in the UI, even for a frame during hydration).
      const entryNames = {};

      for (const e of entries) {
        const uid = e.userId || e.uid || e.id;
        if (uid) {
          uniqueUids.add(uid);
          if (!entryNames[uid]) entryNames[uid] = e.userName || e.displayName || e.gamertag || null;
        }
      }
      for (const h of hostEntries) {
        const uid = h.userId || h.id;
        if (uid) {
          uniqueUids.add(uid);
          if (!entryNames[uid]) entryNames[uid] = h.userName || h.displayName || h.gamertag || null;
        }
      }

      await Promise.all([...uniqueUids].map(async (uid) => {
        if (!uid || profiles[uid]) return;

        try {
          const pub = await getDoc(doc(db, 'public_profiles', uid));
          if (pub.exists()) {
            const d = pub.data();
            updates[uid] = d?.userName || d?.displayName || d?.gamertag || uid;
            metaUpdates[uid] = { badge: d?.profileBadge || 'none', photo: d?.profilePhotoUrl || null };
            return;
          }
        } catch {/* ignore */}

        try {
          const prv = await getDoc(doc(db, 'users', uid));
          if (prv.exists()) {
            const d = prv.data();
            updates[uid] = d?.userName || d?.displayName || d?.gamertag || uid;
            metaUpdates[uid] = { badge: 'none', photo: d?.photoURL || null };
            return;
          }
        } catch {/* ignore */}

        updates[uid] = entryNames[uid] || 'Player';
        metaUpdates[uid] = { badge: 'none', photo: null };
      }));

      if (!cancelled && Object.keys(updates).length) {
        setProfiles(prev => ({ ...prev, ...updates }));
        setEntMeta(prev => ({ ...prev, ...metaUpdates }));
      }
    })();
    return () => { cancelled = true; };
  }, [entries, hostEntries, profiles]);

  /* ---------- Derivations ---------- */
  const entryFeeCents = (ev?.entryFeeCents != null)
    ? toCents(ev.entryFeeCents)
    : toCents((ev?.entryFee || 0) * 100);

  // ---- Crit-entry (post-pivot) -------------------------------------------
  // Crit events are entered with non-cash Crits (1 Crit = $1 equivalent). They
  // reuse the EXACT cash payout structures, just denominated in Crits. We feed
  // critEntryCost into the SAME usePayouts engine (currency-agnostic) so the
  // join-screen ladder is identical to the create-screen ladder, and format all
  // event/contest amounts with fmtMoney (Crits) instead of usd ($).
  const isCritEvent = (ev?.entryCurrency === 'crits') || (ev?.critEntryCost != null);
  const critEntryCost = Math.max(0, Math.floor(Number(ev?.critEntryCost || 0)));
  // The per-unit value handed to usePayouts: whole Crits for Crit events
  // (1 Crit = $1), cents otherwise. Pool/band magnitudes come back in this unit.
  const payoutUnit = isCritEvent ? critEntryCost : entryFeeCents;
  // Currency-aware formatter for event/contest money. For Crit events the value
  // is already in whole Crits; for cash it's cents (matches usd()).
  const fmtMoney = (v) => (isCritEvent ? fmtCrits(v) : usd(v));
  // Per-winner split of a band total. Crits round UP (non-monetary scoreboard,
  // clean/generous numbers); cash rounds to nearest (legacy display behavior).
  const perWinner = (total, winners) => {
    const w = Math.max(1, Number(winners) || 1);
    return isCritEvent ? Math.ceil((total || 0) / w) : Math.round((total || 0) / w);
  };

  const startAt  = toDateLike(ev?.startAt || ev?.startTime || ev?.startsAt);
  const closesAt = toDateLike(ev?.registrationClosesAt || ev?.registrationCloseTime);
  const cdClose  = useCountdown(closesAt);

  const gameKey = String(ev?.game || '').toLowerCase();
  // Open catalog: community/browse-catalog events resolve to the generic
  // profile so the detail page renders their name, formats, and platforms.
  const gp = GAME_PROFILES?.[gameKey] || resolveGameProfile(gameKey, ev?.gameName) || {};
  const fmtRaw = String(ev?.format || ev?.rules?.format || ev?.bestOf || '').toLowerCase();
  // Resolve format key: check formatName first (new events), then legacy matching
  const formatKey = (() => {
    // New events store formatName directly
    if (ev?.formatName && gp?.formats?.[ev.formatName]) return ev.formatName;
    // Legacy fighting game fallback
    if (fmtRaw.includes('5') && gp?.formats?.['Best of 5']) return 'Best of 5';
    if (fmtRaw.includes('3') && gp?.formats?.['Best of 3']) return 'Best of 3';
    return Object.keys(gp?.formats || {})[0] || null;
  })();
  const formatMeta = formatKey ? gp?.formats?.[formatKey] : null;

  const isRacing = (GAME_PROFILES?.[gameKey]?.type === 'racing') || (String(ev?.mode).toLowerCase() === 'racing');

  // Warzone battle royale: single-lobby FFA showdown (event doc = the match).
  // Entries are per-player (teamSize stays 1); squadSize is the IN-GAME
  // grouping (1=Solos .. 4=Quads) used for display + squad-rank payouts.
  const isBattleRoyale = ev?.battleRoyale === true || formatMeta?.battleRoyale === true;
  const squadSize = Math.max(1, Number(ev?.squadSize) || 1);
  const squadLabel = SQUAD_SIZE_LABELS[squadSize] || 'Solos';

  // Madden in-game settings — new events store ev.gameSettings; events created
  // before the settings panel derive a view from their quarter-length format.
  const maddenSettings = gameKey === 'mdn'
    ? (ev?.gameSettings || maddenSettingsFromLegacyFormat(ev?.formatName))
    : null;
  const canEditGameSettings = gameKey === 'mdn'
    && !!user?.uid
    && (ev?.createdByUid === user?.uid || isAdmin)
    && String(ev?.status || 'scheduled').toLowerCase() === 'scheduled';

  const teamSize = useMemo(() => {
    const fromEvent  = Number(ev?.teamSize || 0);
    const fromFormat = Number(formatMeta?.defaultTeamSize || (Array.isArray(formatMeta?.teamSizes) ? formatMeta.teamSizes[0] : 0));
    return Math.max(1, fromEvent || fromFormat || 1);
  }, [ev?.teamSize, formatMeta]);
  
  const requiresTeam = teamSize > 1;
  const entryUnit = String(ev?.entryUnit || 'player');
  const unitLabel = teamSize === 1 ? 'players' : 'teams';

  const entrantsCount = entries.length;
  const capacityCount = useMemo(() => {
    if (isRacing) {
      return Number(
        ev?.entriesExpected ??
        ev?.maxEntrants ??
        ev?.maxEntries ??
        ev?.maxTeams ??
        ev?.bracketSize ??
        0
      ) || 0;
    }
    return Number(
      ev?.entriesExpected ??
      ev?.maxEntrants ??
      ev?.maxEntries ??
      ev?.maxTeams ??
      0
    ) || 0;
  }, [isRacing, ev?.entriesExpected, ev?.maxEntrants, ev?.maxEntries, ev?.maxTeams, ev?.bracketSize]);

  // Find my individual entry (NEW: for team events, this is my personal entry)
  const myEntry = useMemo(() => {
    if (!user?.uid) return null;
    return entries.find(e => (e.userId || e.uid || e.id) === user.uid) || null;
  }, [entries, user?.uid]);

  const canSeeStreamTitle = !!myEntry;

  // NEW: Get user's personal streaming title from their entry
  const myStreamingTitle = useMemo(() => {
    if (!myEntry) return null;
    // Use the user's unique streaming title if available
    return myEntry.streamingTitle || null;
  }, [myEntry]);

  // Required stream title
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!ev) return;
      const local = ev?.requiredStreamTitle;
      if (typeof local === 'string') { setRequiredTitle(local); return; }
      const linkId = ev?.tournamentsId || ev?.tournamentId || ev?.parentId;
      if (linkId) {
        try {
          const snap = await getDoc(doc(db, 'tournaments', String(linkId)));
          if (!cancel && snap.exists()) {
            const v = snap.data()?.requiredStreamTitle;
            if (v) setRequiredTitle(v);
          }
        } catch { /* ignore */ }
      }
    })();
    return () => { cancel = true; };
  }, [ev]);


  const rakeRate = Math.max(0, Math.min(1, Number(ev?.rakePercent ?? 10) / 100));
  const isTournament = coll === 'tournaments';
  
  // Determine mode for proper payout calculation
  const mode = String(ev?.mode || ev?.format || '').toLowerCase();
  const isShowdown = mode === 'showdown';
  const isH2H = mode === 'h2h';

  // A dedicated, PAID host role exists only on events designed for it:
  // GT7 racing/showdown (supportsHost), COD/CDL team events at/above
  // hostMinTeamSize (requiresHost), and Warzone BR lobbies (battleRoyale
  // formats ALWAYS take a CODCaster host despite teamSize 1). Ordinary solo
  // brackets, H2H, and Madden are participant-run (a player just starts the
  // lobby) and have NO paid host — so the "Enter as Host" UI must NOT appear
  // for them.
  const eventHasDedicatedHost = coll === 'tournaments'
    && !isH2H
    && gameKey !== 'mdn'
    && !!gp?.supportsHost
    && (!gp?.requiresHost
      || gp?.formats?.[ev?.formatName]?.battleRoyale === true
      || (Number(ev?.teamSize) || 1) >= (gp?.hostMinTeamSize ?? 1));

  // Use the usePayouts hook which properly handles all event types
  const live = usePayouts({
    ev,
    entrants: entrantsCount,
    entryFeeCents: payoutUnit,
    isTournament,
    isRacing,
    platformFeeRate: rakeRate
  });

  const projected = usePayouts({
    ev,
    entrants: capacityCount || entrantsCount,
    entryFeeCents: payoutUnit,
    isTournament,
    isRacing,
    platformFeeRate: rakeRate
  });

  // Warzone BR: the static wz-* structure this event pays (usePayouts can't
  // resolve cod showdowns - it falls back to the entrant-scaled GT7 bands,
  // which are wrong here). Payout table + Structure row read from this.
  const wzStruct = useMemo(() => {
    if (!isBattleRoyale) return null;
    const psId = String(
      ev?.payoutStructureId
      || (typeof ev?.payoutStructure === 'string' ? ev.payoutStructure : '')
      || ''
    );
    return PAYOUT_STRUCTURES[psId]
      || WZ_PAYOUT_STRUCTURES[psId]
      || WZ_PAYOUT_STRUCTURES[squadSize > 1 ? 'wz-squads-top-5' : 'wz-solos-top-10'];
  }, [isBattleRoyale, ev?.payoutStructureId, ev?.payoutStructure, squadSize]);

  // Build payout rows from stageBands returned by usePayouts hook.
  //
  // Two layout strategies depending on event type:
  //
  // SHOWDOWNS: bands scale by entrant count (post-2026-05-30). At n=5 the
  // live bands are {1, 2, 3, ITM 4-4} but the full-grid projected bands
  // are {1, 2, 3, ITM 4-7}. Keying on "from-to" against the projected
  // layout would orphan the live ITM row. We instead drive the table from
  // LIVE bands (so the rank ranges match what's actually paid right now)
  // and look up "if grid fills" per-position amounts by walking the
  // projected bands and finding the one whose range contains this row's
  // starting rank.
  //
  // EVERYTHING ELSE: bands are static across attendance levels (h2h is
  // winner-take-all, brackets are sized at creation, multi-heat racing
  // structures are pre-computed). The original layout — drive from full-
  // grid projection, look up live by exact range match — works fine.
  const payoutRows = useMemo(() => {
    const fullBands = projected.stageBands || [];
    const nowBands = live.stageBands || [];

    // Warzone BR: render the STATIC wz structure bands (top 10 solos / top 5
    // squads) against the live + full-grid pools - never the entrant-scaled
    // GT7 showdown projection. Squad bands show the SQUAD's share (split
    // among its members at payout time).
    if (isBattleRoyale) {
      const bands = wzStruct?.bands || [];
      if (!bands.length) return [];
      // Crit model: round the band total UP to whole units (mirrors
      // buildStageBands in usePayouts).
      const bandTotal = (b, poolCents) =>
        Math.ceil(Math.max(0, poolCents || 0) * (Number(b.percent) || 0) / 100);
      return bands.map((b) => {
        const from = Number(b.minRank) || 1;
        const to = Number(b.maxRank) || from;
        const winners = Math.max(1, to - from + 1);
        return {
          key: `${from}-${to}`,
          label: b.tier || b.stage || 'Prize',
          winners,
          fullEachCents: perWinner(bandTotal(b, projected.netPoolCents), winners),
          nowEachCents: perWinner(bandTotal(b, live.netPoolCents), winners),
        };
      });
    }

    if (isShowdown) {
      // Drive the table from the FULL-grid projection so every eventual tier
      // is visible from event creation onwards — otherwise the n<2 fallback
      // in getShowdownBandsForEntrants returns just `[Champion 100%]` and
      // the listing reads like winner-take-all even though it's actually a
      // top-7 showdown. Each (Now) is filled per-tier by looking up the live
      // band whose range overlaps each projected band's starting rank, and
      // stays blank for tiers the current attendance doesn't yet pay.
      if (!fullBands.length && !nowBands.length) return [];
      const tableBands = fullBands.length ? fullBands : nowBands;
      return tableBands.map(band => {
        const winners = band.count || 1;
        const fullEachCents = perWinner(band.totalCents, winners);

        let nowEachCents = 0;
        for (const nb of nowBands) {
          if (band.from >= nb.from && band.from <= nb.to) {
            nowEachCents = perWinner(nb.totalCents, nb.count || 1);
            break;
          }
        }

        return {
          key: `${band.from}-${band.to}`,
          label: band.stage,
          winners,
          fullEachCents,
          nowEachCents,
        };
      });
    }

    // Non-showdown path — original projected-driven layout
    if (!fullBands.length) return [];

    const nowMap = {};
    nowBands.forEach(b => {
      const key = `${b.from}-${b.to}`;
      nowMap[key] = b;
    });

    return fullBands.map(band => {
      const key = `${band.from}-${band.to}`;
      const nowBand = nowMap[key];

      const fullTotal = band.totalCents || 0;
      const nowTotal = nowBand?.totalCents || 0;
      const winners = band.count || 1;

      return {
        key,
        label: band.stage,
        winners,
        fullEachCents: perWinner(fullTotal, winners),
        nowEachCents: nowTotal > 0 ? perWinner(nowTotal, winners) : 0,
      };
    });
  }, [projected.stageBands, live.stageBands, isShowdown, isCritEvent,
      isBattleRoyale, wzStruct, projected.netPoolCents, live.netPoolCents]);

  const netNow  = live.netPoolCents || 0;
  const netFull = projected.netPoolCents || netNow;
  
  const psStruct = useMemo(() => {
    if (isBattleRoyale && wzStruct) return wzStruct; // static BR structure
    const psId = live.structureId || projected.structureId;
    if (!psId) return null;
    const key = Object.keys(PAYOUT_STRUCTURES).find(k => k.toLowerCase() === String(psId).toLowerCase());
    return key ? PAYOUT_STRUCTURES[key] : null;
  }, [live.structureId, projected.structureId, isBattleRoyale, wzStruct]);

  // Structure text under the title. Crits ON: the resolved structure, else
  // usePayouts' structureDesc (unchanged). Crits OFF: only the resolved
  // structure's money-free text, never the raw structureDesc (it can carry
  // an event's own payoutDescription).
  const summaryStructText = critsEnabled
    ? (psStruct?.description || projected?.structureDesc || live?.structureDesc)
    : structureText(psStruct, false);

  const bracketMeta = useMemo(() => resolveBracketMeta({
    gameKey,
    bracketSize: Number(ev?.bracketSize || ev?.maxTeams || ev?.maxEntries || capacityCount || 0) || 0,
    type: gp?.type,
  }), [gameKey, ev?.bracketSize, ev?.maxTeams, ev?.maxEntries, capacityCount, gp?.type]);

  const titleText = useMemo(() => {
    let baseTitle = '';
    if (ev?.name) {
      baseTitle = ev.name;
    } else if (bracketMeta?.name) {
      baseTitle = bracketMeta.name;
    } else {
      // PUBLISHER COMPLIANCE: Fallback should NOT include game name
      // Game name is displayed separately in the subtitle
      const mode = ev?.mode || '';
      if (mode === 'h2h') baseTitle = 'H2H Match';
      else if (mode === 'bracket') baseTitle = 'Tournament';
      else if (mode === 'showdown') baseTitle = 'Showdown';
      else if (mode === 'racing') baseTitle = 'Race';
      else baseTitle = 'Event';
    }
    const formatSuffix = formatKey ? ` ${formatKey}` : '';
    if (formatSuffix && !baseTitle.includes(formatKey)) {
      return `${baseTitle} ${formatSuffix}`;
    }
    return baseTitle;
  }, [ev?.name, bracketMeta, ev?.mode, formatKey]);

  // Fill-mode events have no closesAt until they fill — closed only after
  // filledAt is stamped or status moves past 'scheduled'. Other modes use
  // the standard closesAt comparison.
  const isFillModeEvent = ev?.startMode === 'fill';
  const evStatus = String(ev?.status || 'scheduled').toLowerCase();
  const isClosed = isFillModeEvent
    ? (!!ev?.filledAt || (evStatus && evStatus !== 'scheduled'))
    : (!!closesAt && (cdClose.done || Date.now() >= closesAt.getTime()));

  // Warzone BR lobby panels. The host submits the 6-char game code on the
  // EVENT doc (matchId === eventId); entrants/host/admin read it back once
  // it's in and the event is locked/live.
  const viewerIsLobbyHost = !!user?.uid && user.uid === (ev?.hostUserId || ev?.hostUid);
  const showBrNeedsHost = isBattleRoyale && ev?.needsHost === true
    && !['completed', 'finished', 'cancelled', 'canceled'].includes(evStatus);

  // ⚠️ These used to be gated on `isBattleRoyale`, which left a GT7 SHOWDOWN
  // host with NO way to submit the game code at all — the ready modals stay
  // suppressed behind `awaitingRoomId`, so the event could not proceed from the
  // app (found live 2026-08-07, event 15mRI45TVDtBCO9Hb9Xq; the code had to be
  // pushed through the callable by hand).
  //
  // The backend was never BR-specific: submitMatchRoomId handles any
  // event-level FFA via `matchId === eventId` (matchLobbySystem.js), and
  // onSubmitGameCode already passes exactly that. So gate on the EVENT DOC'S
  // OWN LOBBY STATE instead of the format — `awaitingRoomId`/`roomId` only ever
  // exist on event-level FFA docs (BR, showdown, league race), which makes them
  // both the accurate signal and one that can't miss a future format.
  const showBrCodeSubmit = viewerIsLobbyHost
    && ev?.awaitingRoomId === true && !ev?.roomId;
  const showBrCode = !!ev?.roomId
    && ['locked', 'live'].includes(evStatus)
    && (!!myEntry || viewerIsLobbyHost || isAdmin);

  /* ---------- Load team data (for display only) ---------- */
  useEffect(() => {
    (async () => {
      if (!user?.uid) { setMyTeam(null); return; }
      
      try {
        const teamsRef = collection(db, 'teams');
        const [ownSnap, memSnap] = await Promise.all([
          getDocs(query(teamsRef, where('ownerId', '==', user.uid), limit(1))),
          getDocs(query(teamsRef, where('memberIds', 'array-contains', user.uid), limit(1)))
        ]);
        const docSnap = ownSnap?.docs?.[0] || memSnap?.docs?.[0];
        if (docSnap) {
          setMyTeam({ id: docSnap.id, ...docSnap.data() });
        } else {
          setMyTeam(null);
        }
      } catch {
        setMyTeam(null);
      }
    })();
  }, [user?.uid]);

  /* ---------- Actions ---------- */
  
  // Validate prerequisites and show confirmation modal
  const onEnterPress = useCallback(async () => {
    if (!user?.uid) {
      // Guest hit Enter Event — route to signup with a clear "this is why".
      // Previously this just showed a dismissible error and dead-ended.
      showError(
        'Create an account to compete',
        'CritCash events are gated to verified users. Sign up in under a minute to enter this event.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Sign Up', onPress: () => navigation.navigate('SignUp') },
        ]
      );
      return;
    }
    if (!agree) { 
      showError('Agreement required', 'Please accept the Terms to enter.'); 
      return; 
    }
    // Check eligibility (stream key + IGN)
    if (!eligibilityLoading && !isEligible) {
      if (!hasStreamKey) {
        showError(
          'Stream Key Required',
          'You need to set up your CritCash stream key before joining events.\n\nAll participants must stream their gameplay for verification.',
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'Set Up Streaming', 
              onPress: () => navigation.navigate('StreamingAccounts')
            }
          ]
        );
        return;
      }
      if (!hasIGN && gameId) {
        showError(
          ' Game Profile Required',
          isPlatformLocked && hasAnyIGN
            ? `This is a ${eventPlatform}-only event. Add ${eventPlatform} with your in-game name to your game profile to join.`
            : isPlatformLocked
              ? `This is a ${eventPlatform}-only event. Set your in-game name (IGN) for ${eventPlatform} before joining. Your opponents need to know who to look for in the lobby!`
              : 'You need to set your in-game name (IGN) for this game before joining. Your opponents need to know who to look for in the lobby!',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Set Up Now',
              onPress: () => navigation.navigate('GameProfiles', { highlightGame: gameId })
            }
          ]
        );
        return;
      }
    }
    if (closesAt && (cdClose.done || Date.now() >= closesAt.getTime())) {
      showError('Registration closed', `Registration ended at ${fmtDate(closesAt)}.`);
      return;
    }
    if (!ev?.id) return;

    // Check if user has enough balance (Crits for Crit events, cash otherwise)
    if (isCritEvent) {
      // Crits OFF: no balance check (the server debits nothing; an in-flight
      // cost>0 event must not block on a retired balance).
      if (critsEnabled && critBalance < critEntryCost) {
        showError(
          'Not Enough Crits',
          `You need ${fmtCrits(critEntryCost - critBalance)} more to enter this event.\n\nEntry cost: ${fmtCrits(critEntryCost)}\nYour balance: ${fmtCrits(critBalance)}`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Get Crits', onPress: () => navigation.navigate('Subscription') }
          ]
        );
        return;
      }
    } else if (totalBalanceCents < entryFeeCents) {
      showError(
        'Insufficient Balance',
        `You need ${usd(entryFeeCents - totalBalanceCents)} more to enter this event.\n\nEntry fee: ${usd(entryFeeCents)}\nYour balance: ${usd(totalBalanceCents)}`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Add Funds',
            onPress: () => navigation.navigate('Wallet')
          }
        ]
      );
      return;
    }
    
    // All validations passed - show confirmation modal
    setShowEntryConfirmModal(true);
  }, [user?.uid, agree, eligibilityLoading, isEligible, hasStreamKey, hasIGN, hasAnyIGN, isPlatformLocked, eventPlatform, gameId, closesAt, cdClose.done, ev?.id, totalBalanceCents, entryFeeCents, isCritEvent, critBalance, critEntryCost, critsEnabled, showError, navigation]);

  // Actually execute the entry after user confirms
  const confirmEntry = useCallback(async () => {
    setShowEntryConfirmModal(false);

    // NEW: Use the individual team entry function for team events
    const functionName = requiresTeam ? 'enterTeamEventIndividually' : 'joinEventWithDebit';
    const call = httpsCallable(getFunctions(undefined, 'us-central1'), functionName);

    const payload = {
      eventId: ev.id,
      collection: coll,
    };

    // Add teamId if user is on a team (for priority matching)
    if (requiresTeam && myTeam?.id) {
      payload.teamId = myTeam.id;
    }

    // Add idempotency key for non-team events
    if (!requiresTeam) {
      payload.idempotencyKey = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    }

    // Racer-host opt-in (hosted-racer heats): the checkbox copy names the
    // Host Terms, so checking it IS the affirmative Host ToS acceptance.
    if (!requiresTeam && ev?.hostRaces === true && racerHostOptIn) {
      payload.hostOptIn = true;
      payload.hostTosAccepted = true;
    }

    // Named so the schedule-conflict warning can re-run it acknowledged.
    const executeEntry = async (acknowledgeScheduleConflict) => {
    try {
      setJoining(true);
      await call(acknowledgeScheduleConflict ? { ...payload, acknowledgeScheduleConflict: true } : payload);
      showError('Success', requiresTeam
        ? 'Entry confirmed! You\'ll be matched with teammates after registration closes.'
        : 'You\'re entered!'
      );
    } catch (e) {
      const msg = String(e?.message || '');
      const closed = e?.code === 'failed-precondition' || /registration_closed/i.test(msg);

      // Check for IGN-related errors
      if (msg.includes('stream-key-required')) {
        showError(
          'Stream Key Required',
          'You need to set up your CritCash stream key before joining events.',
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'Set Up Streaming', 
              onPress: () => navigation.navigate('StreamingAccounts')
            }
          ]
        );
      } else if (msg.includes('ign-required') || msg.includes('ign-invalid') || msg.includes('platform-ign-required')) {
        const cleanMsg = msg.replace('platform-ign-required:', '').replace('ign-required:', '').replace('ign-invalid:', '');
        showError(
          ' Game Profile Required',
          cleanMsg,
          [
            { text: 'Later', style: 'cancel' },
            { 
              text: 'Set Up Now', 
              onPress: () => navigation.navigate('GameProfiles', { highlightGame: gameId })
            }
          ]
        );
      } else if (msg.includes('schedule-conflict:')) {
        // Soft warning, not a block (must precede the generic failed-
        // precondition `closed` branch): confirm re-runs the entry
        // acknowledged.
        const detail = msg.split('schedule-conflict:')[1]?.trim()
          || 'This event overlaps another event you\'re committed to.';
        showError('Schedule conflict', detail, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Enter anyway', onPress: () => executeEntry(true) },
        ]);
      } else if (msg.includes('host-cannot-race:')) {
        // Must precede the generic failed-precondition `closed` branch.
        showError('Hosts spectate here',
          msg.split('host-cannot-race:')[1]?.trim()
          || "You're this event's host — hosts spectate here.");
      } else if (closed) {
        showError('Registration closed', `Registration ended at ${fmtDate(closesAt)}.`);
      } else if (e?.code === 'already-exists' || msg.includes('already entered')) {
        showError('Already Entered', 'You have already entered this event.');
      } else {
        showError('Enter failed', String(e?.message || e?.code || 'Try again.'));
      }
    } finally {
      setJoining(false);
    }
    };
    await executeEntry(false);
  }, [user?.uid, agree, closesAt, cdClose.done, ev?.id, coll, requiresTeam, myTeam?.id, showError, ev?.hostRaces, racerHostOptIn]);

  const onLeavePress = useCallback(async () => {
    if (!user?.uid || !ev?.id || !myEntry) return;

    try {
      setLeaving(true);

      // NEW: Use individual leave function for team events
      const functionName = requiresTeam ? 'leaveTeamEventIndividually' : 'leaveEventWithRefund';
      const functions = getFunctions(undefined, 'us-central1');

      await httpsCallable(functions, functionName)({
        eventId: ev.id,
        collection: coll,
      });

      showError('Done', critsEnabled ? 'You have been removed and refunded.' : 'You have left the event.');
    } catch (e) {
      const raw = String(e?.message || e?.code || e);
      let friendly = raw;

      if (raw.includes('registration-closed') || raw.includes('event-started')) {
        friendly = 'Registration has closed. You can no longer leave this event.';
      } else if (raw.includes('creator-cannot-leave-with-entrants')) {
        friendly = 'You can\'t leave your own event once others have entered. The event will run as scheduled.';
      } else if (raw.includes('insufficient-funds')) {
        friendly = 'Insufficient balance.';
      } else if (raw.includes('permission-denied')) {
        friendly = 'You don\'t have permission to do that.';
      }

      showError('Unable to leave', friendly);
    } finally {
      setLeaving(false);
    }
  }, [user?.uid, ev?.id, myEntry, coll, requiresTeam, showError, critsEnabled]);

  // Creator-only cancel. Only succeeds while status='scheduled' AND
  // entrantsCount ≤ 1; backend enforces the rule, this is the UI affordance.
  const onCancelEventPress = useCallback(async () => {
    if (!user?.uid || !ev?.id) return;
    try {
      setCancellingEvent(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'cancelEventByCreator')({
        eventId: ev.id,
        collection: coll,
      });
      showError('Event cancelled', critsEnabled ? 'Your event has been cancelled and your entry refunded.' : 'Your event has been cancelled.');
    } catch (e) {
      const raw = String(e?.message || e?.code || e);
      let friendly = raw;
      if (raw.includes('event-has-other-entrants')) {
        friendly = 'Other players have entered. You can no longer cancel this event.';
      } else if (raw.includes('event-already-locked')) {
        friendly = 'This event is already locked. Cancellation is no longer possible.';
      } else if (raw.includes('not-event-creator')) {
        friendly = 'Only the event creator can cancel this event.';
      }
      showError('Unable to cancel event', friendly);
    } finally {
      setCancellingEvent(false);
    }
  }, [user?.uid, ev?.id, coll, showError, critsEnabled]);

  // Creator-only Run It Back — clones this completed event onto next week's
  // same slot and bell-recalls everyone who showed up. Backend gates on the
  // certified fill evaluation; this is the UI affordance.
  const onRunItBackPress = useCallback(() => {
    if (!user?.uid || !ev?.id) return;
    const executeRunItBack = async () => {
      try {
        setRunningItBack(true);
        const functions = getFunctions(undefined, 'us-central1');
        const res = await httpsCallable(functions, 'runItBackEvent')({ eventId: ev.id });
        const newId = res?.data?.eventId;
        if (newId) {
          navigation.navigate('EventDetailScreen', {
            id: newId,
            eventId: newId,
            collection: res?.data?.collection || 'tournaments',
          });
        }
      } catch (e) {
        const raw = String(e?.message || e?.code || e);
        let friendly = raw;
        const prefix = ['run-it-back-locked', 'run-it-back-pending', 'run-it-back-team', 'run-it-back-lead']
          .find((p) => raw.includes(`${p}:`));
        if (prefix) friendly = raw.split(`${prefix}:`)[1]?.trim() || friendly;
        showError('Unable to run it back', friendly);
      } finally {
        setRunningItBack(false);
      }
    };
    showError(
      'Run It Back',
      'Reruns the same event for the same crowd — next week, same time. Everyone who showed up gets the call.',
      [
        { text: 'Not now', style: 'cancel' },
        { text: 'Run It Back', onPress: () => executeRunItBack() },
      ]
    );
  }, [user?.uid, ev?.id, navigation, showError]);

  // Creator/admin-only Madden game-settings edit. Backend enforces the
  // permission + scheduled-status rules; this is the UI affordance.
  const onOpenGameSettingsEditor = useCallback(() => {
    setEditedGameSettings({ ...(maddenSettings || {}) });
    setGameSettingsOpen(true);
  }, [maddenSettings]);

  const onSaveGameSettings = useCallback(async () => {
    if (!user?.uid || !ev?.id || !editedGameSettings) return;
    try {
      setSavingGameSettings(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'updateEventGameSettings')({
        eventId: ev.id,
        gameSettings: editedGameSettings,
      });
      setGameSettingsOpen(false);
      showError('Settings saved', 'The game settings for this event have been updated.');
    } catch (e) {
      const raw = String(e?.message || e?.code || e);
      let friendly = raw;
      if (raw.includes('not-event-creator')) {
        friendly = 'Only the event creator can edit game settings.';
      } else if (raw.includes('event-already-locked')) {
        friendly = 'This event has already started. Game settings can no longer be changed.';
      } else if (raw.includes('game-settings-unsupported')) {
        friendly = 'Game settings can\'t be edited for this game yet.';
      }
      showError('Unable to save settings', friendly);
    } finally {
      setSavingGameSettings(false);
    }
  }, [user?.uid, ev?.id, editedGameSettings, showError]);

  // ============================================================================
  // HOST ENTRY HANDLERS (Phase 3)
  // ============================================================================

  // Before seeding, bracketSize/totalRounds aren't set yet on the tournament doc.
  // Racing events use heatsCount (not bracketSize) to determine elimination rounds.
  // Racing totalRounds = log2(heatsCount) + 1 (elimination heats + finals).
  // Showdowns (GT7 single race, Warzone BR lobby) are always ONE round - the
  // bracket log2 fallback would invent ~6 phantom host rounds off a
  // 50-entrant BR doc.
  const isRacingEvent = ev?.mode === 'racing';
  const heatsCount = ev?.heatsCount || ev?.heatCount || 1;
  const totalRounds = ev?.totalRounds
    || (isShowdown
      ? 1
      : isRacingEvent
        ? Math.ceil(Math.log2(heatsCount)) + 1
        : Math.max(1, Math.ceil(Math.log2(bracketTeams(ev)))));
  const hostSlots = ev?.hostSlots || {};

  // Calculate total host slots needed across all rounds and total filled
  // Round 1 = heatsCount matches, Round 2 = heatsCount/2, ... Finals = 1
  // Total = heatsCount + heatsCount/2 + ... + 1 = 2*heatsCount - 1
  const totalHostSlotsNeeded = (() => {
    let total = 0;
    for (let r = 1; r <= totalRounds; r++) {
      total += hostSlots[`round${r}`]?.total || matchesInRound(ev, r);
    }
    return total;
  })();
  const totalHostSlotsFilled = (() => {
    let total = 0;
    for (let r = 1; r <= totalRounds; r++) {
      total += hostSlots[`round${r}`]?.filled || 0;
    }
    // A host-raced event has NO host-slot ledger to fill: the backend only
    // builds hostSlots for requiresHost games (COD, Marvel Rivals) and
    // multi-heat racing, so a 1-heat GT7 showdown never gets one. The creator
    // is still the host — they're just also on the grid. Without this the page
    // advertised "Host Slots 0/1" on an event whose host had already claimed
    // the role (founder, 2026-08-07: "it doesn't fill in the host spot").
    const hasLedger = Object.keys(hostSlots).length > 0;
    if (!hasLedger && ev?.hostRaces === true && (ev?.hostUserId || ev?.hostUid)) {
      return totalHostSlotsNeeded;
    }
    return total;
  })();

  const onHostEntryPress = useCallback(() => {
    if (!user?.uid) {
      showError('Sign In Required', 'Please sign in to enter as a host.');
      return;
    }
    // Only pre-check rounds that have open slots
    const availableRounds = [];
    for (let i = 1; i <= totalRounds; i++) {
      const slot = hostSlots[`round${i}`];
      const filled = slot?.filled || 0;
      const total = slot?.total || matchesInRound(ev, i);
      if (filled < total) availableRounds.push(i);
    }
    setHostSelectedRounds(availableRounds);
    setShowHostRoundModal(true);
  }, [user?.uid, totalRounds, hostSlots, heatsCount, showError]);

  const submitHostEntry = useCallback(async (tosAccepted, acknowledgeScheduleConflict) => {
    if (!user?.uid || !ev?.id || hostSelectedRounds.length === 0) return;
    try {
      setJoiningAsHost(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'joinEventAsHost')({
        eventId: ev.id,
        selectedRounds: hostSelectedRounds,
        hostTosAccepted: tosAccepted,
        ...(acknowledgeScheduleConflict ? { acknowledgeScheduleConflict: true } : {}),
      });
      setShowHostRoundModal(false);
      setShowHostTosModal(false);
      showError('Host Entry Confirmed', `You are now hosting rounds ${hostSelectedRounds.join(', ')}.`);
    } catch (e) {
      const msg = String(e?.message || e);
      if (msg.includes('already-exists')) {
        showError('Already Entered', 'You are already entered for this event.');
      } else if (msg.includes('schedule-conflict:')) {
        // Soft warning (must precede the generic failed-precondition branch):
        // confirm re-runs the host entry acknowledged.
        const detail = msg.split('schedule-conflict:')[1]?.trim()
          || 'This event overlaps another event you\'re committed to.';
        showError('Schedule conflict', detail, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Host anyway', onPress: () => submitHostEntry(tosAccepted, true) },
        ]);
      } else if (msg.includes('failed-precondition')) {
        showError('Cannot Enter', msg.replace(/^.*?:\s*/, ''));
      } else {
        showError('Host Entry Failed', msg.replace(/^.*?:\s*/, ''));
      }
    } finally {
      setJoiningAsHost(false);
    }
  }, [user?.uid, ev?.id, hostSelectedRounds, showError]);

  const onHostEntryConfirm = useCallback(() => {
    if (!user?.uid || !ev?.id || hostSelectedRounds.length === 0) return;
    submitHostEntry(true);
  }, [user?.uid, ev?.id, hostSelectedRounds, submitHostEntry]);

  const onHostLeavePress = useCallback(async () => {
    if (!user?.uid || !ev?.id || !myHostEntry) return;
    try {
      setLeavingAsHost(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'leaveEventAsHost')({ eventId: ev.id });
      showError('Done', 'You have been removed as host.');
    } catch (e) {
      const msg = String(e?.message || e);
      showError('Cannot Leave', msg.replace(/^.*?:\s*/, ''));
    } finally {
      setLeavingAsHost(false);
    }
  }, [user?.uid, ev?.id, myHostEntry, showError]);

  const toggleHostRound = useCallback((roundNum) => {
    setHostSelectedRounds(prev =>
      prev.includes(roundNum)
        ? prev.filter(r => r !== roundNum)
        : [...prev, roundNum].sort((a, b) => a - b)
    );
  }, []);

  // ============================================================================
  // WARZONE BR HANDLERS (event-level single lobby: matchId === eventId)
  // ============================================================================

  // Claim the unhosted BR lobby. Backend re-checks every gate (participant
  // block, IGN, TOS, needsHost) - this mirrors BracketHubScreen's claim call.
  const onClaimHostSlot = useCallback(async () => {
    if (!user?.uid || !ev?.id) return;
    try {
      setClaimingHostSlot(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'claimUnhostedMatch')({
        eventId: ev.id,
        matchId: ev.id,
        hostTosAccepted: true,
      });
      showError('Lobby claimed', 'You are the host. Create the private match in game, then submit the 6-character game code here.');
    } catch (e) {
      const msg = String(e?.message || e);
      showError('Could not claim host slot', msg.replace(/^.*?:\s*/, '') || 'Try again.');
    } finally {
      setClaimingHostSlot(false);
    }
  }, [user?.uid, ev?.id, showError]);

  // Host submits the 6-char game code - fans the code out to every entrant
  // and opens the ready window (mirrors LeagueDetailScreen's race Room-ID
  // box). The event goes live through the standard ready flow, not here.
  const onSubmitGameCode = useCallback(async () => {
    if (!user?.uid || !ev?.id) return;
    const code = gameCodeInput.trim().toLowerCase();
    if (!/^[a-z0-9]{6}$/.test(code)) {
      showError('Invalid game code', 'Game codes are 6 characters - letters and digits.');
      return;
    }
    try {
      setSubmittingGameCode(true);
      const functions = getFunctions(undefined, 'us-central1');
      await httpsCallable(functions, 'submitMatchRoomId')({
        eventId: ev.id,
        matchId: ev.id,
        roomId: code,
      });
      setGameCodeInput('');
    } catch (e) {
      showError('Game code failed', String(e?.message || 'Try again.'));
    } finally {
      setSubmittingGameCode(false);
    }
  }, [user?.uid, ev?.id, gameCodeInput, showError]);

  const onCopyGameCode = useCallback(async () => {
    const code = String(ev?.roomId || '').toUpperCase();
    if (!code) return;
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(code);
      } else {
        await Clipboard.setStringAsync(code);
      }
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch { /* ignore */ }
  }, [ev?.roomId]);

  /* ---------- Helpers for payouts modal ---------- */
  function groupedStages(fullRows = [], nowRows = [], baseBands = []) {
    const byKey = new Map();
    for (const r of fullRows || []) byKey.set(r.key, { full: r });
    for (const r of nowRows || []) {
      const e = byKey.get(r.key) || {};
      e.now = r;
      byKey.set(r.key, e);
    }
    const stageMap = new Map();
    for (const [key, pair] of byKey.entries()) {
      const r = pair.now || pair.full;
      const stage = r?.stage || 'Finals';
      const g = stageMap.get(stage) || {
        stage,
        stagePercent: r?.stagePercent ?? null,
        heatCount: r?.heatCount ?? 1,
        unitSize: r?.unitSize ?? null,
        rows: [],
      };
      g.rows.push({
        key,
        label: r?.label,
        winners: r?.winners ?? 1,
        fullEach: Math.round(pair.full?.perWinnerCents || 0),
        nowEach: Math.round(pair.now?.perWinnerCents || 0),
      });
      stageMap.set(stage, g);
    }
    const order = (baseBands || []).map(b => b.stage || 'Finals');
    return order.map(s => stageMap.get(s)).filter(Boolean);
  }

  const platform = ev?.platform || '';

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.center}><ActivityIndicator color={COLORS.GOLD} /></View>
        <Footer navigation={navigation} /><NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }
  
  if (!id || ev === null) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.center}><Text style={{ color: COLORS.TEXT }}>Event not available.</Text></View>
        <Footer navigation={navigation} /><NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  const renderActionCard = () => {
    const eventHostUidDoc = ev?.hostUserId || ev?.hostUid || null;
    const viewerIsEventHost = !!user?.uid && eventHostUidDoc === user.uid;
    // Hosted-racer event (hostRaces): the host also takes a PAID racer seat.
    // Before they've entered, fall through to the ENTER card (with a hosting
    // banner) instead of dead-ending on "YOU'RE HOSTING" with no way to pay.
    const hostNeedsRacerSeat = !!myHostEntry && !myEntry && ev?.hostRaces === true
      && viewerIsEventHost && !isClosed;

    // Cancel Event — the creator's exit while the event is still empty. The
    // host cards shadow the participant card that historically owned this
    // button, so creator-hosts need it rendered on their cards too.
    const cancelEventBtn = (ev?.createdByUid === user?.uid && !isClosed && (ev?.entrantsCount || 0) <= 1) ? (
      <TouchableOpacity
        onPress={onCancelEventPress}
        disabled={cancellingEvent}
        style={[styles.leaveBtn, { marginTop: 8, backgroundColor: '#3a1f1f', borderColor: '#7a2f2f' }, cancellingEvent && { opacity: 0.6 }]}
      >
        <Text style={styles.leaveText}>
          {cancellingEvent ? 'Working' : 'Cancel Event'}
        </Text>
      </TouchableOpacity>
    ) : null;

    // Run It Back — the creator's rerun affordance on a completed event. The
    // backend mirrors the fill evaluation's status onto the event doc as
    // fillCertification (fillEvaluations itself is server-only): 'certified'
    // unlocks the button, 'pending_settle' shows a wait note. Team events are
    // excluded (backend rejects team clones for now). Like cancelEventBtn,
    // the host/participant cards shadow the closed card that completed events
    // land on, so it renders on every card the creator sees.
    const fillCertStatus = ev?.fillCertification?.status || null;
    const runItBackEligible = ev?.createdByUid === user?.uid
      && ['completed', 'finished'].includes(evStatus)
      && entryUnit !== 'team';
    const runItBackSection = !runItBackEligible ? null
      : fillCertStatus === 'certified' ? (
        <TouchableOpacity
          onPress={onRunItBackPress}
          disabled={runningItBack}
          style={[styles.enterBtn, { marginTop: 8 }, runningItBack && { opacity: 0.6 }]}
        >
          <Text style={styles.enterText}>
            {runningItBack ? 'Working' : 'Run It Back'}
          </Text>
        </TouchableOpacity>
      ) : fillCertStatus === 'pending_settle' ? (
        <Text style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
          Sellout under review — Run It Back unlocks when it certifies.
        </Text>
      ) : null;

    // HOSTED RACER — both roles held: hosting this event AND racing in it.
    if (myHostEntry && myEntry) {
      return (
        <View style={styles.card}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Ionicons name="shield-checkmark" size={20} color="#FCD000" style={{ marginRight: 8 }} />
            <Text style={styles.cardTitle}>YOU'RE HOSTING & RACING</Text>
          </View>
          <Text style={{ color: '#8fe2a2', fontWeight: '800', marginBottom: 6 }}>
            Your racer seat is confirmed — you also run your lobby as its host.
          </Text>
          <Text style={{ color: '#999', fontSize: 12, marginBottom: 12 }}>
            Tier {myHostEntry.hostTier || 1} Host{critsEnabled ? `  |  Host reward: ${Math.round(HOST_CRIT_REWARD_CONFIG.potPct * 100)}% of your lobby's Crit pool, up to ${HOST_CRIT_REWARD_CONFIG.cap} Crits per round` : ''}
          </Text>
          <TouchableOpacity
            onPress={onLeavePress}
            disabled={leaving || isClosed}
            style={[styles.leaveBtn, (leaving || isClosed) && { opacity: 0.6 }]}
          >
            <Text style={styles.leaveText}>
              {isClosed ? 'Event Locked' : (leaving ? 'Working' : (critsEnabled ? 'Leave & Refund' : 'Leave Event'))}
            </Text>
          </TouchableOpacity>
          {cancelEventBtn}
          {runItBackSection}
        </View>
      );
    }

    // User already entered as HOST (spectating host, or hosted racer whose
    // seat can't be paid right now — e.g. registration closed)
    if (myHostEntry && !hostNeedsRacerSeat) {
      const isCreatorHost = myHostEntry.creatorHost === true;
      return (
        <View style={styles.card}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Ionicons name="shield-checkmark" size={20} color="#FCD000" style={{ marginRight: 8 }} />
            <Text style={styles.cardTitle}>YOU'RE HOSTING</Text>
          </View>
          <Text style={{ color: '#8fe2a2', fontWeight: '800', marginBottom: 6 }}>
            {(myHostEntry.selectedRounds || []).length > 0
              ? `Rounds: ${(myHostEntry.selectedRounds || []).join(', ')}`
              : 'Event host'}
          </Text>
          <Text style={{ color: '#999', fontSize: 12, marginBottom: 12 }}>
            Tier {myHostEntry.hostTier || 1} Host  |  {myHostEntry.status === 'registered' ? 'Awaiting event start' : myHostEntry.status}
          </Text>
          {isCreatorHost ? (
            <>
              <Text style={{ color: '#888', fontSize: 11 }}>
                You created this event as its host — it can't run without you. Cancel the event if you need out.
              </Text>
              {cancelEventBtn}
            </>
          ) : (
            <TouchableOpacity
              onPress={onHostLeavePress}
              disabled={leavingAsHost || isClosed}
              style={[styles.leaveBtn, (leavingAsHost || isClosed) && { opacity: 0.6 }]}
            >
              <Text style={styles.leaveText}>
                {isClosed ? 'Event Locked' : (leavingAsHost ? 'Working' : 'Leave Host Slot')}
              </Text>
            </TouchableOpacity>
          )}
          {runItBackSection}
        </View>
      );
    }

    // User already entered as PARTICIPANT
    if (myEntry) {
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>YOU'RE ENTERED</Text>
          {viewerIsEventHost && ev?.hostRaces === true && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
              <Ionicons name="shield-checkmark" size={16} color="#FCD000" style={{ marginRight: 6 }} />
              <Text style={{ color: '#FCD000', fontSize: 12, fontWeight: '700' }}>
                {ev?.battleRoyale === true
                  ? (critsEnabled
                    ? "You're also this event's host — you'll create the private match and earn the host reward."
                    : "You're also this event's host — you'll create the private match.")
                  : (critsEnabled
                    ? "You're also this event's host — you'll run the race lobby and earn the host reward."
                    : "You're also this event's host — you'll run the race lobby.")}
              </Text>
            </View>
          )}
          {requiresTeam && (
            <View style={styles.teamWarningBox}>
              <View style={styles.teamInfoRow}>
                <Ionicons name="people" size={18} color="#9146FF" style={{ marginRight: 8 }} />
                <Text style={styles.teamWarningText}>
                  {myTeam ? `You'll be matched with your team (${myTeam.name || 'Unnamed Team'})` : 'You\'ll be matched with other players'} after registration closes.
                </Text>
              </View>
              {!myTeam && (
                <View style={[styles.teamInfoRow, { marginTop: 8 }]}>
                  <Ionicons name="information-circle" size={16} color="#888" style={{ marginRight: 6 }} />
                  <Text style={[styles.teamWarningText, { fontSize: 11 }]}>
                    Join a team in "Your Team" for priority matching with teammates.
                  </Text>
                </View>
              )}
            </View>
          )}
          {!requiresTeam && (
            <Text style={{ color:'#8fe2a2', fontWeight:'800', marginBottom: 10 }}>
              Your spot is confirmed. Good luck!
            </Text>
          )}
          <TouchableOpacity
            onPress={onLeavePress}
            disabled={leaving || isClosed}
            style={[styles.leaveBtn, (leaving || isClosed) && { opacity: 0.6 }]}
          >
            <Text style={styles.leaveText}>
              {isClosed ? 'Event Locked' : (leaving ? 'Working' : (critsEnabled ? 'Leave & Refund' : 'Leave Event'))}
            </Text>
          </TouchableOpacity>
          {ev?.createdByUid === user?.uid && !isClosed && (ev?.entrantsCount || 0) <= 1 && (
            <TouchableOpacity
              onPress={onCancelEventPress}
              disabled={cancellingEvent}
              style={[styles.leaveBtn, { marginTop: 8, backgroundColor: '#3a1f1f', borderColor: '#7a2f2f' }, cancellingEvent && { opacity: 0.6 }]}
            >
              <Text style={styles.leaveText}>
                {cancellingEvent ? 'Working' : 'Cancel Event'}
              </Text>
            </TouchableOpacity>
          )}
          {runItBackSection}
        </View>
      );
    }

    // Registration closed
    if (isClosed) {
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>REGISTRATION CLOSED</Text>
          <TouchableOpacity disabled={true} style={[styles.enterBtn, { opacity: 0.6 }]}>
            <Text style={styles.enterText}>Registration Closed</Text>
          </TouchableOpacity>
          {runItBackSection}
        </View>
      );
    }

    // Can enter — dual Participant / Host buttons
    return (
      <View style={styles.card}>
        <Text style={styles.cardTitle}>ENTER EVENT</Text>

        {/* Hosted racer: the event's host takes their paid seat here */}
        {hostNeedsRacerSeat && (
          <View style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            backgroundColor: '#241d00',
            borderWidth: 1,
            borderColor: '#FCD000',
            borderRadius: 8,
            padding: 12,
            marginVertical: 10,
          }}>
            <Ionicons name="shield-checkmark" size={20} color="#FCD000" style={{ marginRight: 10, marginTop: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: '#FCD000', fontWeight: '800', fontSize: 13, marginBottom: 2 }}>
                {ev?.battleRoyale === true ? "PLAYING HOST — you're this event's host" : "HOSTED RACER — you're this event's host"}
              </Text>
              <Text style={{ color: '#d9c98a', fontSize: 12, lineHeight: 17 }}>
                Enter below to take your {ev?.battleRoyale === true ? 'player slot' : 'racer seat'}{critsEnabled && isCritEvent && critEntryCost > 0 ? ` (${fmtCrits(critEntryCost)})` : ''}.{critsEnabled ? ` You keep the host reward — ${Math.round(HOST_CRIT_REWARD_CONFIG.potPct * 100)}% of your lobby's Crit pool, up to ${HOST_CRIT_REWARD_CONFIG.cap} Crits per round hosted.` : ' You still run the lobby as its host.'}
              </Text>
              {cancelEventBtn}
            </View>
          </View>
        )}

        {/* Team event info */}
        {requiresTeam && (
          <View style={styles.teamWarningBox}>
            <View style={styles.teamInfoRow}>
              <Ionicons name="people" size={18} color="#9146FF" style={{ marginRight: 8 }} />
              <Text style={styles.teamWarningTitle}>Team Event ({teamSize}v{teamSize})</Text>
            </View>
            <Text style={styles.teamWarningText}>
              {myTeam 
                ? `You're on ${myTeam.name || 'a team'}. Your team will be matched together if all members enter.`
                : `You're not on a team. You'll be matched with other players to form a pickup team.`
              }
            </Text>
            <View style={[styles.teamInfoRow, { marginTop: 8 }]}>
              <Ionicons name="shuffle" size={14} color="#888" style={{ marginRight: 6 }} />
              <Text style={[styles.teamWarningText, { fontSize: 11 }]}>
                <Text style={{ fontWeight: '700' }}>Pickup Groups:</Text> Solo players are matched into teams at event start. Full teams have priority.
              </Text>
            </View>
          </View>
        )}
        
        {/* GT7 VR Mode warning — visible to BOTH ENTER & PAY and ENTER AS HOST
            flows so racers and hosts both see it before they commit. CritCash
            vision verification only handles standard/cockpit camera output;
            VR Mode breaks both the OCR pass and the win/loss detection. */}
        {gameId === 'gt7' && (
          <View style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            backgroundColor: '#2a0e0e',
            borderWidth: 1,
            borderColor: '#FF6B6B',
            borderRadius: 8,
            padding: 12,
            marginVertical: 10,
          }}>
            <Ionicons name="warning" size={22} color="#FF6B6B" style={{ marginRight: 10, marginTop: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: '#FF6B6B', fontWeight: '800', fontSize: 13, marginBottom: 4 }}>
                VR Mode is NOT supported for GT7 events
              </Text>
              <Text style={{ color: '#f5b3b3', fontSize: 12, lineHeight: 17 }}>
                Race in Standard or Cockpit view. VR Mode breaks the camera output CritCash needs
                to verify lobby setup and detect race results — entries played in VR will be voided.
              </Text>
            </View>
          </View>
        )}

        {/* Agreement checkbox */}
        <TouchableOpacity 
          onPress={() => !isClosed && setAgree(a => !a)} 
          disabled={isClosed} 
          style={styles.checkRow}
        >
          <View style={[styles.checkBox, agree && { backgroundColor: COLORS.GOLD }]}>
            {agree && <Text style={{ color:'#000', fontWeight:'800' }}></Text>}
          </View>
          <Text style={{ color: COLORS.TEXT }}>
            I agree to the Rules & Terms{requiresTeam ? ' and understand taper rules' : ''}.
          </Text>
        </TouchableOpacity>

        {/* Stream Key Warning Banner */}
        {user?.uid && !eligibilityLoading && !hasStreamKey && !myEntry && (
          <TouchableOpacity 
            style={styles.streamKeyWarningBanner}
            onPress={() => navigation.navigate('StreamingAccounts')}
          >
            <Ionicons name="key" size={20} color="#FCD000" style={{ marginRight: 8 }} />
            <View style={styles.warningContent}>
              <Text style={styles.warningTitle}>Stream Key Required</Text>
              <Text style={styles.warningSubtitle}>
                Set up streaming to join events
              </Text>
            </View>
            <Text style={styles.warningArrow}></Text>
          </TouchableOpacity>
        )}

        {/* IGN Warning Banner */}
        {user?.uid && !eligibilityLoading && hasStreamKey && !hasIGN && gameId && !myEntry && (
          <TouchableOpacity 
            style={styles.ignWarningBanner}
            onPress={() => navigation.navigate('GameProfiles', { highlightGame: gameId })}
          >
            <Ionicons name="game-controller" size={20} color="#FCD000" style={{ marginRight: 8 }} />
            <View style={styles.warningContent}>
              <Text style={styles.warningTitle}>
                {isPlatformLocked && hasAnyIGN ? `Add ${eventPlatform} to Your Game Profile` : 'Set Your In-Game Name'}
              </Text>
              <Text style={styles.warningSubtitle}>
                {isPlatformLocked
                  ? `${eventPlatform} players only — a ${eventPlatform} in-game name is required to join`
                  : 'Required to join this event'}
              </Text>
            </View>
            <Text style={styles.warningArrow}></Text>
          </TouchableOpacity>
        )}

        {/* All Set Confirmation */}
        {user?.uid && !eligibilityLoading && isEligible && gameId && (
          <View style={styles.eligibleBanner}>
            <Text style={styles.eligibleIcon}></Text>
            <Text style={styles.eligibleText}>
              Playing as: <Text style={styles.eligibleValue}>{userIGN}</Text>{serviceAccountName ? <Text style={{ color: '#9aa0b4' }}>{' | '}{serviceShortName}: <Text style={styles.eligibleValue}>{serviceAccountName}</Text></Text> : null}
            </Text>
          </View>
        )}

        {/* IGN Set Confirmation */}
        {user?.uid && !eligibilityLoading && hasIGN && gameId && (
          <View style={styles.ignSetBanner}>
            <Text style={styles.ignSetIcon}></Text>
            <Text style={styles.ignSetText}>
              Playing as: <Text style={styles.ignSetValue}>{userIGN}</Text>{serviceAccountName ? <Text style={{ color: '#9aa0b4' }}>{' | '}{serviceShortName}: <Text style={styles.ignSetValue}>{serviceAccountName}</Text></Text> : null}
            </Text>
          </View>
        )}

        {/* Brief Balance Status */}
        {critsEnabled && user?.uid && isCritEvent && critEntryCost > 0 && (
          <View style={[styles.balanceQuickInfo, critBalance >= critEntryCost ? styles.balanceSufficient : styles.balanceInsufficient]}>
            <Text style={styles.balanceQuickText}>
              {critBalance >= critEntryCost
                ? `Balance: ${fmtCrits(critBalance)} Ready to enter`
                : `Balance: ${fmtCrits(critBalance)} Need ${fmtCrits(critEntryCost - critBalance)} more`}
            </Text>
          </View>
        )}
        {user?.uid && !isCritEvent && entryFeeCents > 0 && (
          <View style={[styles.balanceQuickInfo, totalBalanceCents >= entryFeeCents ? styles.balanceSufficient : styles.balanceInsufficient]}>
            <Text style={styles.balanceQuickText}>
              {totalBalanceCents >= entryFeeCents
                ? `Balance: ${usd(totalBalanceCents)} Ready to enter`
                : `Balance: ${usd(totalBalanceCents)} Need ${usd(entryFeeCents - totalBalanceCents)} more`}
            </Text>
          </View>
        )}

        {/* ============================================= */}
        {/* DUAL ENTRY BUTTONS — Participant + Host       */}
        {/* ============================================= */}

        {/* Participant entry button (existing flow) */}
        <TouchableOpacity
          onPress={onEnterPress}
          disabled={joining || !agree || isClosed || (!eligibilityLoading && !isEligible)}
          style={[styles.enterBtn, (joining || !agree || isClosed) && { opacity: 0.6 }]}
        >
          <Text style={styles.enterText}>
            {isClosed
              ? 'Registration closed'
              : (joining
                ? 'Processing'
                : (isCritEvent
                    ? (critsEnabled ? `ENTER · ${fmtCrits(critEntryCost)}` : 'ENTER')
                    : `ENTER & PAY ${usd(entryFeeCents)}`)
              )
            }
          </Text>
        </TouchableOpacity>

        {/* Host entry button — only on events with a dedicated PAID host role
            (GT7 racing/showdown + COD 3v3+). Hidden for H2H, Madden, solo
            brackets, anything participant-run — and for the event's own host
            (they're already hosting; a hosted racer enters as a RACER above). */}
        {/* Host & race events staff lobbies from inside the grid — the
            backend rejects joinEventAsHost on them, so don't offer it. */}
        {eventHasDedicatedHost && ev?.hostRaces !== true && !viewerIsEventHost && !myHostEntry && (() => {
          // Single-lobby showdown/BR events have exactly ONE host; when the
          // doc already carries one (creator-as-host), the slot is taken even
          // if hostSlots bookkeeping lags — treat it as full.
          const singleLobbyHosted = isShowdown && !!(ev?.hostUserId || ev?.hostUid);
          const allSlotsFull = singleLobbyHosted || totalHostSlotsFilled >= totalHostSlotsNeeded;
          return (
            <View style={{ marginTop: 10 }}>
              <TouchableOpacity
                onPress={onHostEntryPress}
                disabled={!user?.uid || isClosed || allSlotsFull || !agree}
                style={[styles.hostEntryBtn, (!user?.uid || isClosed || allSlotsFull || !agree) && { opacity: 0.5 }]}
              >
                <Ionicons name="shield-checkmark" size={16} color="#000" style={{ marginRight: 6 }} />
                <Text style={styles.hostEntryBtnText}>
                  {allSlotsFull ? 'HOST SLOTS FULL' : 'ENTER AS HOST (Free)'}
                </Text>
              </TouchableOpacity>
              <Text style={{ color: '#888', fontSize: 11, textAlign: 'center', marginTop: 4 }}>
                {totalHostSlotsFilled} of {totalHostSlotsNeeded} host slot{totalHostSlotsNeeded !== 1 ? 's' : ''} filled
              </Text>
            </View>
          );
        })()}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.headerWrap}>
        <Header navigation={navigation} />
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        {/* Top stats */}
        <View style={styles.topStats}>
          <View style={styles.stat}>
            <Text style={styles.statLabel}>{isCritEvent ? (critsEnabled ? 'Entry Cost' : 'Entry') : 'Entry Fee'}</Text>
            <Text style={styles.statValue}>{isCritEvent ? (critsEnabled ? fmtCrits(critEntryCost) : 'Free') : usd(entryFeeCents)}</Text>
          </View>
          {!!startAt && (
            <View style={styles.stat}>
              <Text style={styles.statLabel}>Starts</Text>
              <Text style={styles.statValue}>{fmtDate(startAt)}</Text>
            </View>
          )}
          <View style={styles.stat}>
            <Text style={styles.statLabel}>Capacity</Text>
            <Text style={styles.statValue}>
              {capacityCount ? `${entrantsCount}/${capacityCount} ${unitLabel}` : `${entrantsCount} ${unitLabel}`}
            </Text>
          </View>
          {eventHasDedicatedHost && (
            <View style={styles.stat}>
              <Text style={styles.statLabel}>Host Slots</Text>
              <Text style={[styles.statValue, { color: totalHostSlotsFilled >= totalHostSlotsNeeded ? '#8fe2a2' : '#FCD000' }]}>
                {totalHostSlotsFilled}/{totalHostSlotsNeeded}
              </Text>
            </View>
          )}
        </View>

        {/* Title block */}
        <View style={styles.headerBlock}>
          <Text style={styles.title} numberOfLines={2}>{titleText}</Text>
          <Text style={styles.subTitle}>
            {(gp?.displayName || ev?.game || 'Game')}  {(ev?.mode || (coll === 'matches' ? 'Match' : 'bracket'))}
          </Text>
          {!!(summaryStructText || bracketMeta?.description) && (
            <Text style={styles.summary}>
              {bracketMeta?.description ? `${bracketMeta.description}` : ''}
              {summaryStructText
                ? `${bracketMeta?.description ? '  ' : ''}${summaryStructText}`
                : ''}
            </Text>
          )}
          
          {/* Share + Overlay Buttons */}
          <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
            <ShareButton
              eventId={ev?.id}
              collection={coll}
              title={titleText}
              prizePool={critsEnabled ? fmtMoney(netFull || netNow) : ''}
            />
            <OverlayButton eventId={ev?.id} collection={coll} />
          </View>
        </View>

        {/* Coordinator result recording (manual-results events, creator only) */}
        <CoordinatorResultsPanel
          eventId={ev?.id}
          ev={ev}
          uid={user?.uid}
          showError={showError}
        />

        <View style={styles.columns}>
          {/* LEFT */}
          <View style={styles.leftCol}>
            {/* Details */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>DETAILS</Text>
              <Detail
                label="Platform"
                value={ev?.platform || ''}
                subValue={isPlatformLocked
                  ? `${eventPlatform} players only — a ${eventPlatform} game profile is required to enter`
                  : (ev?.platform ? 'Open to all platforms' : undefined)}
              />
              {!!formatMeta && <Detail label="Format" value={formatKey} subValue={formatMeta?.description} />}

              {/* Manual-results events (every non-vision game): joiners see
                  how the result settles before they enter. Heats (mode
                  'racing') are admin-resolved, so they skip the row. */}
              {ev?.manualResults === true && mode !== 'racing' && (
                <Detail
                  label="Results"
                  value="Reported by players"
                  subValue={isShowdown
                    ? 'When the race ends, tap MATCH ENDED and report your finish. Matching reports post instantly, and anything contested gets reviewed.'
                    : `When the match ends, tap MATCH ENDED and report your result. Matching reports post instantly${mode === 'bracket' ? '; the event creator records matches they are not playing in' : ''}, and anything contested gets reviewed.`}
                />
              )}

              {/* Team Format Info */}
              {requiresTeam && (
                <Detail 
                  label="Team Format" 
                  value={`${teamSize}v${teamSize}`} 
                  subValue={ev?.allowPickupGroups !== false ? 'Pickup groups enabled' : 'Pre-formed teams only'}
                />
              )}
              
              {/* Show mode-specific details */}
              {mode === 'showdown' && (isBattleRoyale ? (
                <>
                  <Detail
                    label="Lobby Size"
                    value={`${ev?.gridSize || formatMeta?.maxPlayers || 50} players - ${squadLabel}`}
                    subValue={critsEnabled ? 'Battle royale - placement payouts' : 'Battle royale - ranked by final placement'}
                  />
                  {Number(ev?.minEntrants) > 0 && (
                    <Detail
                      label="Min to Start"
                      value={`${ev.minEntrants} players`}
                      subValue={critsEnabled ? 'Below this at lock, the event auto-cancels and refunds every entry' : 'Below this at lock, the event auto-cancels'}
                    />
                  )}
                </>
              ) : (
                <Detail
                  label="Grid Size"
                  value={ev?.hostRaces
                    ? `${ev?.gridSize || 16} racers — full grid, host races`
                    : `${ev?.gridSize || gp?.maxPlayers || 15} racers + host`}
                  subValue={critsEnabled ? 'Single race - top half paid' : 'Single race - ranked by finishing position'}
                />
              ))}
              {mode === 'racing' && ev?.heatsCount > 1 && (
                <>
                  <Detail
                    label="Grid Size"
                    value={`${ev?.gridSize || gp?.maxPlayers || 15} racers per heat`}
                    subValue={ev?.hostRaces ? 'Hosted racer event — the host races in the heat they run' : undefined}
                  />
                  <Detail label="Heat Count" value={`${ev?.heatsCount} heats`} />
                </>
              )}
              {/* Showdowns (GT7 single race, Warzone BR) have no bracket -
                  the lobby-size row above covers capacity. */}
              {(mode === 'bracket' || (isTournament && !isShowdown)) && (
                <Detail
                  label={requiresTeam ? 'Teams in Bracket' : 'Bracket Size'}
                  value={`${ev?.bracketSize || ev?.maxTeams || capacityCount || 16} ${unitLabel}`}
                  subValue={requiresTeam ? `${(ev?.bracketSize || ev?.maxTeams || capacityCount || 16) * teamSize} total players` : undefined}
                />
              )}

              {/* Madden game settings — visible to everyone; editable by the
                  creator/admin while the event is still scheduled. */}
              {gameKey === 'mdn' && maddenSettings && (
                <>
                  {Object.entries(MADDEN_GAME_SETTINGS).map(([key, def]) => (
                    <Detail
                      key={key}
                      label={def.label}
                      value={def.type === 'minutes' ? `${maddenSettings[key]} Min` : String(maddenSettings[key])}
                    />
                  ))}
                  {canEditGameSettings && (
                    <TouchableOpacity
                      onPress={onOpenGameSettingsEditor}
                      style={{
                        backgroundColor: '#1a1e35',
                        borderRadius: 8,
                        paddingVertical: 10,
                        paddingHorizontal: 14,
                        marginTop: 10,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Ionicons name="settings-outline" size={16} color="#FCD000" style={{ marginRight: 8 }} />
                      <Text style={{ color: '#FCD000', fontWeight: '700', fontSize: 13 }}>Edit Game Settings</Text>
                    </TouchableOpacity>
                  )}
                </>
              )}

              {/* GT7 / Racing-specific details */}
              {ev?.tracks?.length > 0 && (
                ev.tracks.length === 1 ? (
                  <Detail label="Track" value={ev.tracks[0]} />
                ) : (
                  <>
                    {ev.tracks.map((t, i) => (
                      <Detail
                        key={i}
                        label={i === ev.tracks.length - 1 ? 'Finals' : `R${i + 1}`}
                        value={t}
                      />
                    ))}
                  </>
                )
              )}
              {ev?.carCategory && (
                <Detail
                  label="Vehicle Category"
                  value={
                    gp?.roomSettings?.common?.carCategory?.options?.find(o => o.key === ev.carCategory)?.label
                    || ev.carCategory.toUpperCase()
                  }
                />
              )}
              {/* Race shape — laps, pit stops, tire change. Each resolves
                  through a fallback chain so the host's overrides (saved on
                  ev.lobbySettings.raceSettings) take precedence over legacy
                  top-level fields, which take precedence over the format
                  preset's defaults. */}
              {(() => {
                // Endurance races store raceTimeMinutes instead of raceLaps —
                // show the configured duration in that case.
                const raceTime = ev?.lobbySettings?.raceSettings?.raceTimeMinutes;
                if (typeof raceTime === 'number' && raceTime > 0) {
                  const h = Math.floor(raceTime / 60);
                  const m = raceTime % 60;
                  const formatted = raceTime < 60
                    ? `${raceTime} min`
                    : (m === 0 ? `${h}h` : `${h}h ${m}m`);
                  return <Detail label="Race Duration" value={formatted} />;
                }
                const laps = ev?.lobbySettings?.raceSettings?.raceLaps
                  ?? ev?.laps
                  ?? gp?.formats?.[ev?.formatName]?.laps;
                return laps ? <Detail label="Laps" value={`${laps} Laps`} /> : null;
              })()}
              {(() => {
                const pits = ev?.lobbySettings?.raceSettings?.minPitStops
                  ?? ev?.minPitStops
                  ?? gp?.formats?.[ev?.formatName]?.minPitStops;
                if (pits === undefined || pits === null) return null;
                return <Detail label="Min. Pit Stops" value={String(pits)} />;
              })()}
              {(() => {
                const tire = ev?.lobbySettings?.raceSettings?.requiredTireTypeChange
                  ?? ev?.requiredTireTypeChange
                  ?? gp?.formats?.[ev?.formatName]?.requiredTireTypeChange;
                if (tire === undefined) return null;
                return <Detail label="Required Tire Type Change" value={tire ? 'On' : 'Off'} />;
              })()}
              {gameKey === 'gt7' && (
                <Detail label="Room Mode" value="Practice/Qualifier/Race" />
              )}

              {/* View Race Settings button — GT7 */}
              {(ev?.lobbySettings || gameKey === 'gt7') && (
                <TouchableOpacity
                  onPress={() => setRaceSettingsOpen(true)}
                  style={{
                    backgroundColor: '#1a1e35',
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    marginTop: 10,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="settings-outline" size={16} color="#FCD000" style={{ marginRight: 8 }} />
                  <Text style={{ color: '#FCD000', fontWeight: '700', fontSize: 13 }}>View Race Settings</Text>
                </TouchableOpacity>
              )}

              {!!psStruct && <Detail label="Structure" value={structureName(psStruct, critsEnabled) || ''} subValue={structureText(psStruct, critsEnabled)} />}
              {!!closesAt && (
                <Detail
                  label="Registration Closes"
                  value={fmtDate(closesAt)}
                  subValue={
                    isClosed
                      ? 'Registration closed'
                      : `in ${String(cdClose.d).padStart(2,'0')}d ${String(cdClose.h).padStart(2,'0')}h ${String(cdClose.m).padStart(2,'0')}m ${String(cdClose.sec).padStart(2,'0')}s`
                  }
                />
              )}
            </View>

            {/* Warzone BR: needs-host claim banner. "No host, no event" -
                unclaimed by the deadline cancels + refunds everyone. */}
            {showBrNeedsHost && (
              <View style={[styles.card, { borderColor: '#FCD000' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Ionicons name="alert-circle" size={18} color="#FCD000" style={{ marginRight: 8 }} />
                  <Text style={styles.cardTitle}>HOST NEEDED</Text>
                </View>
                <Text style={{ color: '#bfc6da', fontSize: 12, lineHeight: 17, marginBottom: 10 }}>
                  This lobby has no CODCaster host yet. The host spectates (never
                  plays, never takes a player slot), creates the private match,
                  and submits the game code. If no host claims the lobby in time,
                  the event is cancelled{critsEnabled ? ' and every entry refunded' : ''}.
                </Text>
                {!!user?.uid && !myEntry && (
                  <TouchableOpacity
                    onPress={onClaimHostSlot}
                    disabled={claimingHostSlot}
                    style={[styles.hostEntryBtn, claimingHostSlot && { opacity: 0.6 }]}
                  >
                    <Ionicons name="shield-checkmark" size={16} color="#FCD000" style={{ marginRight: 6 }} />
                    <Text style={styles.hostEntryBtnText}>
                      {claimingHostSlot ? 'CLAIMING' : 'CLAIM HOST SLOT (Free)'}
                    </Text>
                  </TouchableOpacity>
                )}
                {!!myEntry && (
                  <Text style={styles.dimNote}>
                    Entrants can't host - hosting is a spectator role.
                  </Text>
                )}
              </View>
            )}

            {/* Warzone BR: game code (host submits, lobby reads). */}
            {showBrCode && (
              <View style={[styles.card, { borderColor: '#2a4e2a' }]}>
                <Text style={styles.cardTitle}>GAME CODE</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ color: '#7FD4FF', fontSize: 24, fontWeight: '900', letterSpacing: 3 }}>
                    {String(ev.roomId).toUpperCase()}
                  </Text>
                  <TouchableOpacity
                    onPress={onCopyGameCode}
                    style={{
                      flexDirection: 'row', alignItems: 'center', gap: 6,
                      borderWidth: 1, borderColor: '#2a2f4a', borderRadius: 8,
                      paddingVertical: 6, paddingHorizontal: 10,
                    }}
                  >
                    <Ionicons name="copy-outline" size={14} color="#FCD000" />
                    <Text style={{ color: '#FCD000', fontWeight: '700', fontSize: 12 }}>
                      {codeCopied ? 'Copied!' : 'Copy'}
                    </Text>
                  </TouchableOpacity>
                </View>
                <Text style={styles.dimNote}>
                  Join the private match in game with this code.
                  {squadSize > 1 ? ` ${squadLabel} - form your squad in the lobby.` : ''}
                </Text>
              </View>
            )}
            {showBrCodeSubmit && (
              <View style={[styles.card, { borderColor: '#FCD000' }]}>
                <Text style={styles.cardTitle}>SUBMIT GAME CODE</Text>
                <Text style={{ color: '#bfc6da', fontSize: 12, lineHeight: 17, marginBottom: 8 }}>
                  You host this lobby. Create the private match in game, then
                  share the 6-character game code - every entrant is notified
                  and the ready window opens. The event goes live once the
                  players have readied up.
                </Text>
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                  <TextInput
                    style={{
                      flex: 1, backgroundColor: '#0f1115', borderColor: '#2a2f36',
                      borderWidth: 1, borderRadius: 8, paddingHorizontal: 10,
                      paddingVertical: 8, color: '#fff', fontSize: 13,
                    }}
                    value={gameCodeInput}
                    onChangeText={setGameCodeInput}
                    placeholder="6-char game code"
                    placeholderTextColor="#666"
                    maxLength={6}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    onPress={onSubmitGameCode}
                    disabled={submittingGameCode}
                    style={[styles.enterBtn, { paddingHorizontal: 14 }, submittingGameCode && { opacity: 0.6 }]}
                  >
                    {submittingGameCode
                      ? <ActivityIndicator color="#000" size="small" />
                      : <Text style={styles.enterText}>Share Code</Text>}
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Host Earnings Preview — only on events with a dedicated PAID host
                role (GT7 racing/showdown + COD 3v3+). Hidden for solo brackets,
                H2H, Madden — those are participant-run with no host pay. */}
            {eventHasDedicatedHost && (
              <HostEarningsPreview event={ev} navigation={navigation} myHostEntry={myHostEntry} myEntry={myEntry} myHostProfile={myHostProfile} critsEnabled={critsEnabled} />
            )}

            {/* Action card */}
            {renderActionCard()}

            {/* Back */}
            <TouchableOpacity
              onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
              style={styles.backBtn}
            >
              <Text style={styles.backText}>Back</Text>
            </TouchableOpacity>
          </View>

          {/* RIGHT */}
          <View style={styles.rightCol}>
            {/* Payouts — Crits OFF hides the whole card (every value in it is
                a Crit pool / Crit tier); the schedule, bracket and heats cards
                around it are untouched. */}
            {critsEnabled && (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>PRIZE PAYOUTS</Text>

              <View style={styles.poolsRow}>
                <View style={styles.poolBox}>
                  <Text style={styles.poolLabel}>Full Field</Text>
                  <Text style={styles.poolValue}>{fmtMoney(projected.netPoolCents)}</Text>
                </View>
                <View style={styles.poolBox}>
                  <Text style={styles.poolLabel}>Current</Text>
                  <Text style={styles.poolValue}>{fmtMoney(live.netPoolCents)}</Text>
                </View>
              </View>

              <Pressable onPress={() => setPayoutsOpen(true)} style={styles.ctaBtn}>
                <Text style={styles.ctaBtnText}>View payout breakdown</Text>
              </Pressable>

              {/* Show top payout previews for all event types */}
              {(() => {
                // For racing events with many tiers, show highlights
                if (isRacing && payoutRows.length > 8) {
                  // Get Finals winners (Champion, 2nd, 3rd, etc.)
                  const finalsRows = payoutRows.filter(r => r.label && !r.label.includes('Heat of'));
                  const topFinalsRows = finalsRows.slice(0, 3);
                  
                  // Get top Heat tier
                  const heatRows = payoutRows.filter(r => r.label && r.label.includes('Heat of'));
                  const topHeatRow = heatRows[0];
                  
                  // Calculate ranges
                  const amounts = payoutRows.map(r => r.fullEachCents).filter(n => n > 0);
                  const minPayout = amounts.length ? Math.min(...amounts) : 0;
                  const maxPayout = amounts.length ? Math.max(...amounts) : 0;
                  const totalPaidSpots = payoutRows.reduce((sum, r) => sum + (r.winners || 0), 0);
                  
                  return (
                    <>
                      <View style={styles.table}>
                        <View style={styles.tr}>
                          <Text style={[styles.th, { flex: 1.6 }]}>Top Prizes</Text>
                          <Text style={[styles.th, { textAlign: 'right' }]}>Per Winner</Text>
                        </View>
                        {topFinalsRows.map((r) => (
                          <View key={r.key} style={styles.tr}>
                            <Text style={[styles.td, { flex: 1.6 }]}>
                              {r.label}
                            </Text>
                            <Text style={[styles.td, { textAlign: 'right' }]}>
                              {r.fullEachCents ? fmtMoney(r.fullEachCents) : ''}
                            </Text>
                          </View>
                        ))}
                        {topHeatRow && (
                          <View key={topHeatRow.key} style={styles.tr}>
                            <Text style={[styles.td, { flex: 1.6, fontSize: 11 }]}>
                              {topHeatRow.label} <Text style={{ color: '#9aa0b4' }}>({topHeatRow.winners} winners)</Text>
                            </Text>
                            <Text style={[styles.td, { textAlign: 'right' }]}>
                              {topHeatRow.fullEachCents ? fmtMoney(topHeatRow.fullEachCents) : ''}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={styles.dimNote}>
                        Large event: {totalPaidSpots.toLocaleString()} paid spots. Payouts range from {fmtMoney(minPayout)} to {fmtMoney(maxPayout)}. Tap for complete breakdown.
                      </Text>
                    </>
                  );
                }
                
                // For other events or smaller racing events, show top 5-6 rows
                const displayRows = payoutRows.slice(0, isRacing ? 6 : 5);
                if (displayRows.length === 0) {
                  return (
                    <Text style={styles.dimNote}>
                      Payout structure will be displayed once event details are finalized.
                    </Text>
                  );
                }
                
                return (
                  <>
                    <View style={styles.table}>
                      <View style={styles.tr}>
                        <Text style={[styles.th, { flex: 1.2 }]}>Place</Text>
                        <Text style={[styles.th, { flex: 0.8, textAlign: 'center' }]}>Winners</Text>
                        <Text style={[styles.th, { textAlign: 'right' }]}>Each (Full)</Text>
                        <Text style={[styles.th, { textAlign: 'right', marginLeft: 12 }]}>Each (Now)</Text>
                      </View>
                      {displayRows.map((r) => {
                        if (!r.fullEachCents && !r.nowEachCents) return null;
                        return (
                          <View key={r.key} style={styles.tr}>
                            <Text style={[styles.td, { flex: 1.2 }]}>
                              {r.label}
                            </Text>
                            <Text style={[styles.td, { flex: 0.8, textAlign: 'center', color: '#9aa0b4' }]}>
                              {r.winners > 1 ? r.winners.toLocaleString() : '1'}
                            </Text>
                            <Text style={[styles.td, { textAlign: 'right' }]}>
                              {r.fullEachCents ? fmtMoney(r.fullEachCents) : ''}
                            </Text>
                            <Text style={[styles.td, { textAlign: 'right', marginLeft: 12 }]}>
                              {r.nowEachCents ? fmtMoney(r.nowEachCents) : ''}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                    <Text style={styles.dimNote}>
                      {isRacing
                        ? `Multi-stage racing payouts. ${payoutRows.length} prize tiers across ${ev?.heatsCount || 0} heats. View breakdown for all tiers.`
                        : isBattleRoyale
                          ? (squadSize > 1
                              ? 'Placement payouts - the top 5 squads share the Crit pool. A squad\'s share splits evenly among its squadmates.'
                              : 'Placement payouts - the top 10 finishers each earn the amount shown.')
                          : (isCritEvent
                              ? 'Each winner in a tier receives the Crits shown — the same tiered structure as cash, in Crits.'
                              : 'Each winner in a tier receives the amount shown. Platform fee (10%) already deducted.')
                      }
                    </Text>
                  </>
                );
              })()}

            </View>
            )}

            {/* Payouts Modal - Enhanced for racing events */}
            <Modal
              visible={payoutsOpen}
              transparent
              animationType="slide"
              onRequestClose={() => setPayoutsOpen(false)}
            >
              <View style={styles.sheetBackdrop}>
                <View style={styles.sheetCard}>
                  <View style={styles.sheetHeader}>
                    <Text style={styles.sheetTitle}>
                      Prize Breakdown{payoutRows.length > 0 ? ` (${payoutRows.length} tiers)` : ''}
                    </Text>
                    <Pressable onPress={() => setPayoutsOpen(false)} hitSlop={12}>
                      <Text style={styles.sheetClose}>Close</Text>
                    </Pressable>
                  </View>
                  <ScrollView style={{ maxHeight: 520 }}>
                    {(() => {
                      // Group racing payouts by stage for better readability
                      if (isRacing && payoutRows.length > 8) {
                        const finalsRows = payoutRows.filter(r => !r.label?.includes('Heat of'));
                        const heatRows = payoutRows.filter(r => r.label?.includes('Heat of'));
                        
                        // Group heats by their stage name
                        const heatGroups = {};
                        heatRows.forEach(r => {
                          const match = r.label?.match(/^(Heat of [\d,]+)/);
                          const groupName = match ? match[1] : 'Other Heats';
                          if (!heatGroups[groupName]) heatGroups[groupName] = [];
                          heatGroups[groupName].push(r);
                        });
                        
                        return (
                          <>
                            {/* Finals Section */}
                            {finalsRows.length > 0 && (
                              <>
                                <Text style={styles.sectionHeader}>Finals</Text>
                                <View style={styles.table}>
                                  <View style={styles.tr}>
                                    <Text style={[styles.th, { flex: 1.2 }]}>Tier</Text>
                                    <Text style={[styles.th, { flex: 0.8, textAlign: 'center' }]}>Winners</Text>
                                    <Text style={[styles.th, { textAlign: 'right' }]}>Each Gets</Text>
                                  </View>
                                  {finalsRows.map(r => (
                                    <View key={r.key} style={styles.tr}>
                                      <Text style={[styles.td, { flex: 1.2 }]}>{r.label}</Text>
                                      <Text style={[styles.td, { flex: 0.8, textAlign: 'center', color: '#9aa0b4' }]}>
                                        {(r.winners || 1).toLocaleString()}
                                      </Text>
                                      <Text style={[styles.td, { textAlign: 'right' }]}>
                                        {r.fullEachCents ? fmtMoney(r.fullEachCents) : ''}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </>
                            )}
                            
                            {/* Heat Sections */}
                            {Object.entries(heatGroups).map(([groupName, rows]) => (
                              <View key={groupName}>
                                <Text style={styles.sectionHeader}>{groupName}</Text>
                                <View style={styles.table}>
                                  <View style={styles.tr}>
                                    <Text style={[styles.th, { flex: 1.2 }]}>Tier</Text>
                                    <Text style={[styles.th, { flex: 0.8, textAlign: 'center' }]}>Winners</Text>
                                    <Text style={[styles.th, { textAlign: 'right' }]}>Each Gets</Text>
                                  </View>
                                  {rows.map(r => {
                                    const tierOnly = r.label?.replace(/^Heat of [\d,]+ - /, '') || r.label;
                                    return (
                                      <View key={r.key} style={styles.tr}>
                                        <Text style={[styles.td, { flex: 1.2 }]}>{tierOnly}</Text>
                                        <Text style={[styles.td, { flex: 0.8, textAlign: 'center', color: '#9aa0b4' }]}>
                                          {(r.winners || 1).toLocaleString()}
                                        </Text>
                                        <Text style={[styles.td, { textAlign: 'right' }]}>
                                          {r.fullEachCents ? fmtMoney(r.fullEachCents) : ''}
                                        </Text>
                                      </View>
                                    );
                                  })}
                                </View>
                              </View>
                            ))}
                          </>
                        );
                      }
                      
                      // Standard display for non-racing or small events
                      return (
                        <View style={styles.table}>
                          <View style={styles.tr}>
                            <Text style={[styles.th, { flex: 1.2 }]}>Place</Text>
                            <Text style={[styles.th, { flex: 0.8, textAlign: 'center' }]}>Winners</Text>
                            <Text style={[styles.th, { textAlign: 'right' }]}>Each (Full)</Text>
                            <Text style={[styles.th, { textAlign: 'right', marginLeft: 12 }]}>Each (Now)</Text>
                          </View>
                          {payoutRows.map((r) => {
                            if (!r.fullEachCents && !r.nowEachCents) return null;
                            return (
                              <View key={r.key} style={styles.tr}>
                                <Text style={[styles.td, { flex: 1.2 }]}>
                                  {r.label}
                                </Text>
                                <Text style={[styles.td, { flex: 0.8, textAlign: 'center', color: '#9aa0b4' }]}>
                                  {r.winners > 1 ? r.winners.toLocaleString() : '1'}
                                </Text>
                                <Text style={[styles.td, { textAlign: 'right' }]}>
                                  {r.fullEachCents ? fmtMoney(r.fullEachCents) : ''}
                                </Text>
                                <Text style={[styles.td, { textAlign: 'right', marginLeft: 12 }]}>
                                  {r.nowEachCents ? fmtMoney(r.nowEachCents) : ''}
                                </Text>
                              </View>
                            );
                          })}
                        </View>
                      );
                    })()}
                    <View style={{ marginTop: 16, paddingTop: 12, borderTopWidth: 1, borderTopColor: '#1a1e35' }}>
                      <Text style={[styles.dimNote, { textAlign: 'center' }]}>
                        Each player reaching a tier receives the amount shown.{'\n'}
                        Example: If 2,048 players reach Round 2 and the payout is {fmtMoney(isCritEvent ? 5 : 432)},{'\n'}
                        then each of those 2,048 players receives {fmtMoney(isCritEvent ? 5 : 432)}.
                      </Text>
                    </View>
                  </ScrollView>
                </View>
              </View>
            </Modal>

            {/* Legacy streaming title hidden 2026-06-17 — stream-title workflow being
                decommissioned (see project_stream_title_legacy memory). Plumbing left in place. */}

            {/* Heats — multi-heat racing events: your heat, per-heat status,
                host/claim, and the Room ID once the heat's host submits it */}
            {evIsHeatsEvent && heatMatches.length > 0 && (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>HEATS</Text>
                <View style={{ gap: 10 }}>
                  {heatMatches.map((m) => {
                    const viewerInHeat = !!user?.uid
                      && Array.isArray(m.participantUids) && m.participantUids.includes(user.uid);
                    const heatHostUid = m.hostUserId || m.hostUid || null;
                    const viewerHostsHeat = !!user?.uid && heatHostUid === user.uid;
                    const hostPlayer = (m.players || []).find((p) => p.userId === heatHostUid);
                    const hostName = heatHostUid
                      ? (profiles[heatHostUid] || hostPlayer?.displayName || 'Host')
                      : null;
                    const terminal = ['completed', 'cancelled'].includes(String(m.status));
                    const statusLabel = m.status === 'completed' ? 'FINISHED'
                      : m.status === 'cancelled' ? 'CANCELLED'
                        : m.status === 'live' ? 'LIVE'
                          : m.status === 'locked'
                            ? (m.needsHost ? 'NEEDS HOST' : m.awaitingRoomId ? 'AWAITING ROOM ID' : 'READY UP')
                            : 'SCHEDULED';
                    const statusColor = m.status === 'live' ? '#ff5a5a'
                      : m.status === 'completed' ? '#8fe2a2'
                        : m.status === 'cancelled' ? '#777'
                          : m.needsHost ? '#FCD000' : '#9aa0b4';
                    // Backend re-checks every claim gate. Hosted-racer events
                    // staff heats from INSIDE the grid (the 16-seat lobby has
                    // no room for a spectating host), so the button goes to
                    // the racers seeded in the heat; everywhere else it goes
                    // to spectating hosts (no entry).
                    const canClaim = !terminal && m.needsHost === true && !!user?.uid
                      && (ev?.hostRaces === true ? (!!myEntry && viewerInHeat) : !myEntry);
                    const roundLabel = (m.totalRounds || ev?.totalRounds) > 1
                      ? `R${m.round || 1} · ` : '';
                    const p1Placement = Array.isArray(m.heatPlacements)
                      ? m.heatPlacements.find((p) => p.position === 1) : null;
                    const p1Player = p1Placement
                      ? (m.players || []).find((p) => p.userId === p1Placement.userId) : null;
                    return (
                      <View key={m.id} style={{
                        borderWidth: 1,
                        borderColor: viewerInHeat ? '#FCD000' : '#1a1e35',
                        borderRadius: 8,
                        padding: 10,
                      }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Text style={{ color: '#E7ECFF', fontWeight: '900' }}>
                            {roundLabel}Heat {m.heatNumber || m.matchNumber}
                            {viewerInHeat && <Text style={{ color: '#FCD000', fontSize: 11 }}>  YOUR HEAT</Text>}
                          </Text>
                          <Text style={{ color: statusColor, fontSize: 11, fontWeight: '800' }}>{statusLabel}</Text>
                        </View>
                        <Text style={{ color: '#9aa0b4', fontSize: 12, marginTop: 3 }}>
                          {(m.players || []).length} racer{(m.players || []).length !== 1 ? 's' : ''}
                          {hostName ? `  |  Host: ${hostName}${viewerHostsHeat ? ' (you)' : ''}${ev?.hostRaces === true && viewerInHeat && viewerHostsHeat ? ' — racing' : ''}` : ''}
                          {m.status === 'completed' && p1Player ? `  |  P1: ${p1Player.displayName || 'Racer'}` : ''}
                        </Text>
                        {!terminal && m.roomId && (viewerInHeat || viewerHostsHeat) && (
                          <Text style={{ color: '#8fe2a2', fontSize: 12, marginTop: 4, fontWeight: '700' }}>
                            Room ID: {String(m.roomId).toUpperCase()}
                          </Text>
                        )}
                        <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                          {canClaim && (
                            <TouchableOpacity
                              onPress={() => onClaimHeat(m.id)}
                              disabled={claimingHeatId === m.id}
                              style={[styles.hostEntryBtn, { paddingVertical: 8 }, claimingHeatId === m.id && { opacity: 0.6 }]}
                            >
                              <Ionicons name="shield-checkmark" size={14} color="#FCD000" style={{ marginRight: 4 }} />
                              <Text style={[styles.hostEntryBtnText, { fontSize: 12 }]}>
                                {claimingHeatId === m.id ? 'CLAIMING'
                                  : (ev?.hostRaces === true && viewerInHeat ? 'HOST YOUR HEAT' : 'CLAIM AS HOST')}
                              </Text>
                            </TouchableOpacity>
                          )}
                          {(viewerInHeat || viewerHostsHeat) && !terminal && (
                            <TouchableOpacity
                              onPress={() => navigation.navigate('MatchLobby', { matchId: m.id, eventId: id })}
                              style={{ borderWidth: 1, borderColor: '#2a2f4a', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 }}
                            >
                              <Text style={{ color: '#E7ECFF', fontSize: 12, fontWeight: '800' }}>OPEN LOBBY</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}

            {/* Entrants */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>
                {`ENTRANTS${capacityCount ? ` (${entrantsCount} / ${capacityCount})` : ` (${entrantsCount})`}`}
              </Text>
              <View style={{ gap: 10 }}>
                {entries.length === 0 && <Text style={styles.dimNote}>No entrants yet.</Text>}
                {entries.map((e) => {
                  const uid = e.userId || e.uid || e.id;
                  const name = profiles[uid] || e.userName || e.displayName || e.gamertag || 'Player';
                  const teamName = e.teamName || (e.teamId ? `Team ${e.teamId.slice(0, 6)}` : null);
                  const meta = entMeta[uid] || {};
                  const inits = String(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
                  // Hosted racer: this entrant also hosts (event host or a
                  // racer who claimed their own heat's host slot). Open-catalog
                  // manualResults grids auto-stamp the creator as coordinator —
                  // not a self-selected host, so no chip there.
                  const entrantHosts = (ev?.manualResults !== true && uid === (ev?.hostUserId || ev?.hostUid))
                    || hostEntries.some((h) => (h.userId || h.id) === uid);

                  return (
                    <View key={e.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#1a1e35' }}>
                      <FounderBadge size={30} box={30} active={meta.badge === 'founder'}>
                        {meta.photo ? (
                          <Image source={{ uri: meta.photo }} style={styles.entAvatar} />
                        ) : (
                          <View style={[styles.entAvatar, styles.entAvatarInit]}>
                            <Text style={styles.entAvatarTxt}>{inits}</Text>
                          </View>
                        )}
                      </FounderBadge>
                      <Text style={{ color:'#E7ECFF', fontWeight:'900', flex: 1, minWidth: 0 }} numberOfLines={1}>
                        {name}
                        {teamName && <Text style={{ color: '#9aa0b4', fontWeight: '600' }}>  {teamName}</Text>}
                        {entrantHosts && <Text style={{ color: '#FCD000', fontWeight: '800', fontSize: 11 }}>  HOST</Text>}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>

            {/* Hosts — hidden for Madden (mdn = direct EA invite, no hosting/rewards) */}
            {!isH2H && coll === 'tournaments' && gameKey !== 'mdn' && (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>
                  {`HOSTS (${totalHostSlotsFilled} / ${totalHostSlotsNeeded} slots)`}
                </Text>

                {/* Per-round slot breakdown */}
                <View style={{ marginBottom: 12 }}>
                  {Array.from({ length: totalRounds }, (_, i) => i + 1).map(roundNum => {
                    const slotKey = `round${roundNum}`;
                    const filled = hostSlots[slotKey]?.filled || 0;
                    const total = hostSlots[slotKey]?.total || matchesInRound(ev, roundNum);
                    const isFull = filled >= total;
                    // Single-round events (showdowns/BR lobbies) label their
                    // one slot R1, not "Finals".
                    const roundLabel = totalRounds === 1 ? 'R1' : roundNum === totalRounds ? 'Finals' : roundNum === totalRounds - 1 ? 'Semis' : `R${roundNum}`;
                    return (
                      <View key={roundNum} style={{ marginBottom: 8 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                          <Text style={{ color: '#9aa0b4', fontSize: 12, fontWeight: '700' }}>{roundLabel}</Text>
                          <Text style={{ color: isFull ? '#8fe2a2' : '#FCD000', fontSize: 12, fontWeight: '700' }}>
                            {filled}/{total}
                          </Text>
                        </View>
                        <View style={{ height: 4, backgroundColor: '#1a1e35', borderRadius: 2, overflow: 'hidden' }}>
                          <View style={{
                            height: '100%',
                            width: `${Math.min(100, (filled / Math.max(1, total)) * 100)}%`,
                            backgroundColor: isFull ? '#8fe2a2' : '#FCD000',
                            borderRadius: 2,
                          }} />
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* Host list */}
                <View style={{ gap: 10 }}>
                  {hostEntries.length === 0 && <Text style={styles.dimNote}>No hosts registered yet.</Text>}
                  {hostEntries.map((h) => {
                    const uid = h.userId || h.id;
                    const name = profiles[uid] || h.userName || h.displayName || h.gamertag || 'Player';
                    const rounds = (h.selectedRounds || []).sort((a, b) => a - b);
                    const hostAlsoRaces = h.hostRaces === true
                      || (ev?.hostRaces === true && uid === (ev?.hostUserId || ev?.hostUid));
                    return (
                      <View key={h.id} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#1a1e35' }}>
                        <Text style={{ color: '#E7ECFF', fontWeight: '900' }}>
                          {name}
                          {hostAlsoRaces && <Text style={{ color: '#FCD000', fontWeight: '800', fontSize: 11 }}>  RACING</Text>}
                        </Text>
                        <Text style={{ color: '#9aa0b4', fontSize: 12, marginTop: 2 }}>
                          {rounds.length > 0
                            ? `Round${rounds.length !== 1 ? 's' : ''} ${rounds.join(', ')}`
                            : (h.creatorHost ? 'Event host' : 'No rounds selected')} | Tier {h.hostTier || 1}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Race Settings Modal — GT7 */}
      <Modal
        visible={raceSettingsOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setRaceSettingsOpen(false)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheetCard}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Race Lobby Settings</Text>
              <Pressable onPress={() => setRaceSettingsOpen(false)} hitSlop={12}>
                <Text style={styles.sheetClose}>Close</Text>
              </Pressable>
            </View>
            <ScrollView style={{ maxHeight: 520 }}>
              {(() => {
                // Event docs store flat value strings. The constants file now
                // holds the richer {value,label,options} shape, so extract on
                // fallback for old events that pre-date the migration.
                const settings = ev?.lobbySettings
                  || (gameKey === 'gt7' ? extractLobbyValues(GT7_LOBBY_SETTINGS) : null);
                if (!settings) {
                  return <Text style={{ color: '#888', padding: 12 }}>No settings available.</Text>;
                }

                return Object.entries(settings).map(([sectionKey, sectionData]) => (
                  <View key={sectionKey} style={{ marginBottom: 16 }}>
                    <Text style={{
                      color: '#FCD000',
                      fontSize: 13,
                      fontWeight: '700',
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      marginTop: 8,
                      borderBottomWidth: 1,
                      borderBottomColor: '#2a2f36',
                    }}>
                      {GT7_SETTING_SECTION_LABELS[sectionKey] || sectionKey}
                    </Text>
                    {Object.entries(sectionData).map(([key, value]) => (
                      <View key={key} style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderBottomWidth: 1,
                        borderBottomColor: '#1a1e24',
                      }}>
                        <Text style={{ color: '#9aa0b4', fontSize: 12, flex: 1 }}>
                          {GT7_SETTING_KEY_LABELS[key] || key}
                        </Text>
                        <Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: '600', textAlign: 'right', maxWidth: '55%' }}>
                          {String(value)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ));
              })()}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Madden Game Settings Editor — creator/admin, scheduled events only */}
      <Modal
        visible={gameSettingsOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setGameSettingsOpen(false)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheetCard}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Edit Game Settings</Text>
              <Pressable onPress={() => setGameSettingsOpen(false)} hitSlop={12}>
                <Text style={styles.sheetClose}>Close</Text>
              </Pressable>
            </View>
            <ScrollView style={{ maxHeight: 520 }}>
              {editedGameSettings && Object.entries(MADDEN_GAME_SETTINGS).map(([key, def]) => (
                <View key={key} style={{ paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#1a1e24' }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: '#9aa0b4', fontSize: 12 }}>{def.label}</Text>
                    {def.type === 'minutes' && (
                      <Text style={{ color: '#FCD000', fontSize: 12, fontWeight: '700' }}>
                        {editedGameSettings[key]} Min
                      </Text>
                    )}
                  </View>
                  {def.type === 'minutes' ? (
                    Platform.OS === 'web' ? (
                      <input
                        type="range"
                        min={String(def.min)}
                        max={String(def.max)}
                        value={editedGameSettings[key]}
                        onChange={(e) => setEditedGameSettings(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                        style={{ width: '100%', accentColor: '#FCD000', marginTop: 8 }}
                      />
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
                        <TouchableOpacity
                          onPress={() => setEditedGameSettings(prev => ({ ...prev, [key]: Math.max(def.min, (prev?.[key] || def.default) - 1) }))}
                          style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}
                        >
                          <Text style={{ color: '#FCD000', fontSize: 18 }}>-</Text>
                        </TouchableOpacity>
                        <Text style={{ color: '#fff', minWidth: 70, textAlign: 'center', fontSize: 14 }}>{editedGameSettings[key]} Min</Text>
                        <TouchableOpacity
                          onPress={() => setEditedGameSettings(prev => ({ ...prev, [key]: Math.min(def.max, (prev?.[key] || def.default) + 1) }))}
                          style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}
                        >
                          <Text style={{ color: '#FCD000', fontSize: 18 }}>+</Text>
                        </TouchableOpacity>
                      </View>
                    )
                  ) : (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, gap: 6 }}>
                      {def.options.map((opt) => {
                        const active = editedGameSettings[key] === opt;
                        return (
                          <TouchableOpacity
                            key={opt}
                            onPress={() => setEditedGameSettings(prev => ({ ...prev, [key]: opt }))}
                            style={{
                              paddingHorizontal: 12,
                              paddingVertical: 6,
                              borderRadius: 16,
                              borderWidth: 1,
                              borderColor: active ? '#FCD000' : '#2a2f36',
                              backgroundColor: active ? 'rgba(252, 208, 0, 0.12)' : 'transparent',
                            }}
                          >
                            <Text style={{ color: active ? '#FCD000' : '#c9d1d9', fontSize: 12, fontWeight: active ? '700' : '400' }}>
                              {opt}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </View>
              ))}
              <Text style={{ color: '#888', fontSize: 11, paddingHorizontal: 12, paddingTop: 10 }}>
                Everyone entered will see the updated settings. Settings lock once the event starts.
              </Text>
              <TouchableOpacity
                onPress={onSaveGameSettings}
                disabled={savingGameSettings}
                style={{
                  backgroundColor: '#FCD000',
                  borderRadius: 8,
                  paddingVertical: 12,
                  margin: 12,
                  alignItems: 'center',
                  opacity: savingGameSettings ? 0.6 : 1,
                }}
              >
                {savingGameSettings ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={{ color: '#000', fontWeight: '800', fontSize: 14 }}>Save Settings</Text>
                )}
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Entry Confirmation Modal */}
      <Modal visible={showEntryConfirmModal} transparent animationType="fade">
        <Pressable 
          style={styles.confirmModalBackdrop} 
          onPress={() => setShowEntryConfirmModal(false)}
        >
          <Pressable style={styles.confirmModalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.confirmModalTitle}>Confirm Entry</Text>
            
            {/* Event Info */}
            <View style={styles.confirmModalEventBox}>
              <Text style={styles.confirmModalEventName} numberOfLines={2}>{titleText}</Text>
              <Text style={styles.confirmModalEventGame}>
                {gp?.displayName || ev?.game || 'Game'} - {ev?.mode || 'bracket'}
              </Text>
            </View>
            
            {/* Team Info - Only show for team events */}
            {requiresTeam && (
              <View style={styles.teamModalInfoBox}>
                <View style={styles.teamModalHeader}>
                  <Ionicons name="people" size={16} color="#9146FF" style={{ marginRight: 6 }} />
                  <Text style={styles.teamModalTitle}>{teamSize}v{teamSize} Team Event</Text>
                </View>
                <Text style={styles.teamModalText}>
                  {myTeam 
                    ? `Your team: ${myTeam.name || 'Unnamed Team'}`
                    : 'You\'ll be matched with other solo players'
                  }
                </Text>
              </View>
            )}
            
            {/* Entry Fee — Crits OFF: the seat is free, no cost line */}
            {!(isCritEvent && !critsEnabled) && (
            <View style={styles.confirmModalFeeBox}>
              <Text style={styles.confirmModalFeeLabel}>{isCritEvent ? 'Entry Cost' : 'Entry Fee'}{requiresTeam ? ' (per player)' : ''}</Text>
              <Text style={styles.confirmModalFeeAmount}>{isCritEvent ? fmtCrits(critEntryCost) : usd(entryFeeCents)}</Text>
            </View>
            )}

            {/* Payment Breakdown - cash gift balance only (Crit entry spends Crits, no split) */}
            {!isCritEvent && giftBalanceCents > 0 && (
              <View style={styles.confirmModalPaymentBox}>
                <Text style={styles.confirmModalPaymentTitle}>Payment Breakdown</Text>
                
                {/* How much from gift balance */}
                <View style={styles.confirmModalPaymentRow}>
                  <Text style={styles.confirmModalPaymentLabel}>
                    From Gift Card Balance:
                  </Text>
                  <Text style={[styles.confirmModalPaymentValue, { color: '#E91E63' }]}>
                    {usd(Math.min(giftBalanceCents, entryFeeCents))}
                  </Text>
                </View>
                
                {/* How much from wallet (if needed) */}
                {entryFeeCents > giftBalanceCents && (
                  <View style={styles.confirmModalPaymentRow}>
                    <Text style={styles.confirmModalPaymentLabel}>
                      From Wallet Balance:
                    </Text>
                    <Text style={[styles.confirmModalPaymentValue, { color: '#2ECC71' }]}>
                      {usd(entryFeeCents - giftBalanceCents)}
                    </Text>
                  </View>
                )}
                
                <Text style={styles.confirmModalPaymentNote}>
                  Gift card balance is always used first
                </Text>
              </View>
            )}
            
            {/* Rules Reminder */}
            <View style={styles.confirmModalRulesBox}>
              <Text style={styles.confirmModalRulesTitle}>Quick Reminders</Text>
              <Text style={styles.confirmModalRulesText}>You must stream your gameplay via CritCash</Text>
              <Text style={styles.confirmModalRulesText}>Be ready to play at the scheduled start time</Text>
              <Text style={styles.confirmModalRulesText}>Follow all game rules and tournament guidelines</Text>
              {requiresTeam && (
                <Text style={[styles.confirmModalRulesText, { color: '#9146FF' }]}>
                  All team members must stream and ready up
                </Text>
              )}
            </View>
            
            {/* Racer-host opt-in (hosted-racer heats): standing for the whole
                event; selection is earliest-entry-first when a heat locks. */}
            {/* Never on BR: the single lobby's host is the playing creator —
                there is no per-round racer-host selection to opt into. */}
            {ev?.hostRaces === true && !requiresTeam && ev?.battleRoyale !== true && (
              <TouchableOpacity
                onPress={() => setRacerHostOptIn((v) => !v)}
                style={{
                  flexDirection: 'row', alignItems: 'flex-start', gap: 10,
                  borderWidth: 1, borderRadius: 8, padding: 10, marginBottom: 10,
                  borderColor: racerHostOptIn ? '#FCD000' : '#2a2f4a',
                  backgroundColor: racerHostOptIn ? 'rgba(252,208,0,0.08)' : 'transparent',
                }}
              >
                <Ionicons
                  name={racerHostOptIn ? 'checkbox' : 'square-outline'}
                  size={20} color={racerHostOptIn ? '#FCD000' : '#9aa0b4'}
                />
                <View style={{ flex: 1 }}>
                  <Text style={{ color: '#E7ECFF', fontSize: 13, fontWeight: '700' }}>
                    I want to race, and I accept if I'm selected as host for the round
                  </Text>
                  <Text style={{ color: '#9aa0b4', fontSize: 11, marginTop: 3 }}>
                    Heats in this event are hosted by racers in the lobby. Opting in stands
                    for the whole event; if selected (earliest entry first), you create the
                    GT7 room for your heat and race in it. Checking this accepts the{' '}
                    <Text
                      style={{ color: '#5bc4d8', textDecorationLine: 'underline' }}
                      onPress={() => openTermsOfService(navigation, { onBeforeNavigate: () => setShowEntryConfirmModal(false) })}
                    >
                      Host Terms
                    </Text>.
                  </Text>
                </View>
              </TouchableOpacity>
            )}

            {/* TOS Agreement */}
            <View style={styles.confirmModalTosBox}>
              <Text style={styles.confirmModalTosText}>
                By entering, you confirm that you agree to the{' '}
                <Text style={styles.confirmModalTosLink} onPress={() => openTermsOfService(navigation, { onBeforeNavigate: () => setShowEntryConfirmModal(false) })}>
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text style={styles.confirmModalTosLink} onPress={() => openTermsOfService(navigation, { onBeforeNavigate: () => setShowEntryConfirmModal(false) })}>
                  Competition Rules
                </Text>
                .
              </Text>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity
                style={styles.confirmModalCancelBtn}
                onPress={() => setShowEntryConfirmModal(false)}
              >
                <Text style={styles.confirmModalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmModalConfirmBtn}
                onPress={confirmEntry}
                disabled={joining}
              >
                <Text style={styles.confirmModalConfirmText}>
                  {joining ? 'Processing...' : (isCritEvent ? (critsEnabled ? `Spend ${fmtCrits(critEntryCost)} & Enter` : 'Enter') : `Pay ${usd(entryFeeCents)} & Enter`)}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Host Round Selection Modal */}
      <Modal visible={showHostRoundModal} transparent animationType="fade">
        <Pressable style={styles.modalOverlay} onPress={() => setShowHostRoundModal(false)}>
          <Pressable style={styles.hostRoundModal} onPress={e => e.stopPropagation()}>
            <Text style={{ color: '#FCD000', fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
              SELECT YOUR ROUNDS
            </Text>
            <Text style={{ color: '#999', fontSize: 12, marginBottom: 16 }}>
              {critsEnabled ? 'Host up to 1 match per round. More rounds = more rewards.' : 'Host up to 1 match per round.'}
            </Text>

            <ScrollView style={{ maxHeight: 260 }}>
              {Array.from({ length: totalRounds }, (_, i) => i + 1).map(roundNum => {
                const slotKey = `round${roundNum}`;
                const slot = hostSlots[slotKey] || {};
                const matchesThisRound = matchesInRound(ev, roundNum);
                const filledSlots = slot.filled || 0;
                const totalSlots = slot.total || matchesThisRound;
                const isFull = filledSlots >= totalSlots;
                const isSelected = hostSelectedRounds.includes(roundNum);
                const isFinals = roundNum === totalRounds;

                // Hide rounds that are full
                if (isFull) return null;

                return (
                  <TouchableOpacity
                    key={roundNum}
                    onPress={() => toggleHostRound(roundNum)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 12,
                      marginBottom: 6,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: isSelected ? '#FCD000' : '#555',
                      backgroundColor: isSelected ? '#1a1700' : '#111',
                    }}
                  >
                    <View style={{
                      width: 22, height: 22, borderRadius: 4,
                      borderWidth: 2, borderColor: isSelected ? '#FCD000' : '#666',
                      backgroundColor: isSelected ? '#FCD000' : 'transparent',
                      alignItems: 'center', justifyContent: 'center', marginRight: 10,
                    }}>
                      {isSelected && <Ionicons name="checkmark" size={14} color="#000" />}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: '#FFF', fontWeight: '600', fontSize: 14 }}>
                        Round {roundNum}{isFinals ? '  FINALS' : ''}
                      </Text>
                      <Text style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                        {matchesThisRound} match{matchesThisRound !== 1 ? 'es' : ''} | {totalSlots - filledSlots} slot{totalSlots - filledSlots !== 1 ? 's' : ''} open
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 }}>
              <TouchableOpacity
                onPress={() => setShowHostRoundModal(false)}
                style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#555', marginRight: 8, alignItems: 'center' }}
              >
                <Text style={{ color: '#FFF', fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={onHostEntryConfirm}
                disabled={joiningAsHost || hostSelectedRounds.length === 0}
                style={{
                  flex: 2, paddingVertical: 12, borderRadius: 8, alignItems: 'center',
                  backgroundColor: hostSelectedRounds.length > 0 ? '#FCD000' : '#555',
                  opacity: joiningAsHost ? 0.6 : 1,
                }}
              >
                <Text style={{ color: '#000', fontWeight: 'bold' }}>
                  {joiningAsHost ? 'Processing...' : `CONFIRM HOST ENTRY (${hostSelectedRounds.length} round${hostSelectedRounds.length !== 1 ? 's' : ''})`}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

/* ---------- UI Bits ---------- */
const Detail = ({ label, value, subValue }) => (
  <View style={styles.detailRow}>
    <Text style={styles.detailLabel}>{label}</Text>
    <View style={{ alignItems: 'flex-end', maxWidth: '60%' }}>
      <Text style={styles.detailValue}>{value || ''}</Text>
      {!!subValue && <Text style={styles.detailSubValue}>{subValue}</Text>}
    </View>
  </View>
);

/* ---------- Share Button ---------- */
const ShareButton = ({ eventId, collection, title, prizePool }) => {
  const [copied, setCopied] = useState(false);
  
  const getShareUrl = () => {
    return `https://critcash.com/event/${eventId}?collection=${collection}`;
  };
  
  const getShareMessage = () => {
    const url = getShareUrl();
    if (prizePool) {
      return `${title}\n Crit Pool: ${prizePool}\n\nJoin the competition!\n${url}`;
    }
    return `${title}\n\nJoin the competition on CritCash!\n${url}`;
  };
  
  const handleShare = async () => {
    const url = getShareUrl();
    const message = getShareMessage();
    
    // Try native share first (works on mobile and some browsers)
    if (Platform.OS !== 'web' || (typeof navigator !== 'undefined' && navigator.share)) {
      try {
        if (Platform.OS === 'web' && navigator.share) {
          await navigator.share({
            title: title,
            text: message,
            url: url,
          });
          return;
        } else {
          const result = await Share.share({
            message: message,
            url: url,
            title: title,
          });
          
          if (result.action === Share.sharedAction) {
            return;
          }
        }
      } catch (error) {
        console.log('Share cancelled or failed:', error);
      }
    }
    
    // Fall back to clipboard copy
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(url);
      } else {
        await Clipboard.setStringAsync(url);
      }
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };
  
  return (
    <TouchableOpacity 
      style={shareStyles.button}
      onPress={handleShare}
      activeOpacity={0.7}
    >
      <Text style={shareStyles.icon}>{copied ? '' : ''}</Text>
      <Text style={shareStyles.text}>{copied ? 'Link Copied!' : 'Share Event'}</Text>
    </TouchableOpacity>
  );
};

const shareStyles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1e35',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#2a2e45',
    gap: 6,
    marginTop: 12,
  },
  icon: {
    fontSize: 16,
    color: '#FCD000',
  },
  text: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});

/* ---------- Overlay URL Button ---------- */
const OverlayButton = ({ eventId, collection }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const url = `https://critcash.com/overlay?id=${eventId}&collection=${collection}`;
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(url);
      } else {
        await Clipboard.setStringAsync(url);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy overlay URL:', err);
    }
  };

  return (
    <TouchableOpacity
      style={overlayBtnStyles.button}
      onPress={handleCopy}
      activeOpacity={0.7}
    >
      <Ionicons name="tv-outline" size={16} color="#FCD000" />
      <Text style={overlayBtnStyles.text}>
        {copied ? 'Overlay URL Copied!' : 'Stream Overlay'}
      </Text>
    </TouchableOpacity>
  );
};

const overlayBtnStyles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a2e1a',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#2a4e2a',
    gap: 6,
    marginTop: 12,
  },
  text: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});

/* ---------- Styles ---------- */
const styles = StyleSheet.create({
  headerWrap: { zIndex: 1000, elevation: 1000 },
  scroll: { zIndex: 0 },
  safe: { flex: 1, backgroundColor: COLORS.BG },
  container: { padding: 16, paddingBottom: 96 },

  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },

  topStats: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  stat: { flex: 1, backgroundColor: COLORS.PANEL, borderRadius: 10, padding: 12, borderWidth: 1, borderColor: COLORS.STROKE },
  statLabel: { color: COLORS.MUTED, fontSize: 12, marginBottom: 4 },
  statValue: { color: '#fff', fontSize: 16, fontWeight: '800' },

  headerBlock: { marginBottom: 12, alignItems: 'center' },
  title: { color: COLORS.GOLD, fontSize: 24, fontWeight: '900', textAlign: 'center' },
  subTitle: { color: '#ddd', fontSize: 14, marginTop: 4, textAlign: 'center' },
  summary: { color: '#bfc6da', fontSize: 13, marginTop: 6, textAlign: 'center' },

  backBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a2f4a',
    backgroundColor: '#0f1223',
    marginTop: 6,
  },
  backText: { color: '#E7ECFF', fontWeight: '900' },

  columns: { flexDirection: 'row', gap: 12 },
  leftCol: { flex: 1 },
  rightCol: { flex: 1 },

  card: { backgroundColor: COLORS.PANEL, borderRadius: 12, padding: 14, marginBottom: 12, borderWidth: 1, borderColor: COLORS.STROKE },
  cardTitle: { color: '#E7ECFF', fontSize: 13, fontWeight: '900', marginBottom: 8, letterSpacing: 0.3 },

  detailRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#1a1e35' },
  detailLabel: { color: '#9aa0b4', fontSize: 12, fontWeight: '700' },
  detailValue: { color: '#E7ECFF', fontSize: 14, fontWeight: '800' },
  detailSubValue: { color: COLORS.GOLD, fontSize: 12, fontWeight: '800', marginTop: 2 },

  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  checkBox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: COLORS.GOLD, alignItems: 'center', justifyContent: 'center' },

  enterBtn: { backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12, alignItems: 'center' },
  enterText: { color: '#000', fontWeight: '900', fontSize: 14, letterSpacing: 0.3 },

  hostEntryBtn: {
    backgroundColor: '#2a2a00', borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', justifyContent: 'center', flexDirection: 'row',
    borderWidth: 1, borderColor: '#FCD000',
  },
  hostEntryBtnText: { color: '#FCD000', fontWeight: '900', fontSize: 13, letterSpacing: 0.3 },

  hostRoundModal: {
    backgroundColor: '#1a1a1a', borderRadius: 16, padding: 20,
    width: '90%', maxWidth: 420, borderWidth: 1, borderColor: '#333',
  },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center', alignItems: 'center', padding: 20,
  },

  leaveBtn: { backgroundColor: '#32395d', borderRadius: 10, paddingVertical: 12, alignItems: 'center', borderWidth: 1, borderColor: '#3a416d' },
  leaveText: { color: '#E7ECFF', fontWeight: '900', fontSize: 14, letterSpacing: 0.3 },

  poolsRow: { flexDirection: 'row', gap: 10, marginBottom: 8 },
  poolBox: { flex: 1, backgroundColor: '#0f1630', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: '#1a2145' },
  poolLabel: { color: '#94a1d3', fontSize: 12, marginBottom: 4, fontWeight: '700' },
  poolValue: { color: '#E7ECFF', fontSize: 18, fontWeight: '900' },

  table: { marginTop: 6 },
  tr: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#1a1e35' },
  th: { flex: 1, color: '#9aa0b4', fontSize: 12, fontWeight: '800' },
  td: { flex: 1, color: '#E7ECFF', fontSize: 13, fontWeight: '700' },

  streamTitle: { color: '#E7ECFF', fontSize: 13, fontWeight: '800', backgroundColor: '#0d1330', padding: 10, borderRadius: 8, borderWidth: 1, borderColor: '#222a55' },
  dimNote: { color: '#9aa0b4', fontSize: 11, marginTop: 6 },
  entAvatar: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#222' },
  entAvatarInit: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#243A4A' },
  entAvatarTxt: { color: '#7FD4FF', fontWeight: '900', fontSize: 12 },

  ctaBtn: { marginTop: 8, backgroundColor: '#1a5cff', paddingVertical: 10, borderRadius: 10, alignItems: 'center' },
  ctaBtnText: { color: '#fff', fontWeight: '700' },

  sheetBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheetCard: { backgroundColor: '#0E1220', padding: 16, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '88%' },
  sheetHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  sheetTitle: { color: '#E7ECFF', fontWeight: '900', fontSize: 16 },
  sheetClose: { color: '#9aa0b4', fontWeight: '700' },
  
  // Section headers for grouped modal content
  sectionHeader: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '900',
    marginTop: 16,
    marginBottom: 8,
    letterSpacing: 0.5,
  },

  // NEW: Team warning styles
  teamWarningBox: {
    backgroundColor: '#1a2040',
    borderLeftWidth: 3,
    borderLeftColor: '#FCD000',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  teamWarningTitle: {
    color: '#FCD000',
    fontWeight: '900',
    fontSize: 13,
    marginBottom: 6,
  },
  teamWarningText: {
    color: '#c6cbe0',
    fontSize: 12,
    lineHeight: 18,
  },
  // IGN Warning Banner
  ignWarningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2a1a00',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#FCD00050',
  },
  ignWarningIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  ignWarningContent: {
    flex: 1,
  },
  ignWarningTitle: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },
  ignWarningSubtitle: {
    color: '#b39600',
    fontSize: 12,
    marginTop: 2,
  },
  ignWarningArrow: {
    color: '#FCD000',
    fontSize: 18,
    fontWeight: '600',
  },
  
  // IGN Set Confirmation
  ignSetBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a1f0a',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#00A86B30',
  },
  ignSetIcon: {
    color: '#00A86B',
    fontSize: 16,
    fontWeight: '700',
    marginRight: 10,
  },
  ignSetText: {
    flex: 1,
    color: '#888',
    fontSize: 13,
  },
  ignSetValue: {
    color: '#00A86B',
    fontWeight: '700',
  },
    // Stream Key Warning Banner
  streamKeyWarningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#9146FF50',
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
  },
  warningSubtitle: {
    color: '#ccc',
    fontSize: 12,
    marginTop: 2,
  },
  warningArrow: {
    color: '#FFF',
    fontSize: 18,
    marginLeft: 8,
  },

  // Eligible banner
  eligibleBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a1f0a',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#00A86B30',
  },
  eligibleIcon: {
    color: '#00A86B',
    fontSize: 16,
    fontWeight: '700',
    marginRight: 10,
  },
  eligibleText: {
    flex: 1,
    color: '#888',
    fontSize: 13,
  },
  eligibleValue: {
    color: '#00A86B',
    fontWeight: '700',
  },
  
  // Quick Balance Status
  balanceQuickInfo: {
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  balanceSufficient: {
    backgroundColor: '#0a1f0a',
  },
  balanceInsufficient: {
    backgroundColor: '#1f0a0a',
  },
  balanceQuickText: {
    color: '#aaa',
    fontSize: 13,
    textAlign: 'center',
  },
  
  // Entry Confirmation Modal
  confirmModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModalContent: {
    backgroundColor: '#1a1e2e',
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: '#2a3040',
  },
  confirmModalTitle: {
    color: '#FCD000',
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 16,
  },
  confirmModalEventBox: {
    backgroundColor: '#0f1220',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#1a2040',
  },
  confirmModalEventName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 4,
  },
  confirmModalEventGame: {
    color: '#8a8fa8',
    fontSize: 13,
    textAlign: 'center',
  },
  confirmModalFeeBox: {
    backgroundColor: '#FCD00015',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#FCD00030',
    alignItems: 'center',
  },
  confirmModalFeeLabel: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  confirmModalFeeAmount: {
    color: '#FCD000',
    fontSize: 28,
    fontWeight: '800',
  },
  confirmModalPaymentBox: {
    backgroundColor: '#E91E6310',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E91E6330',
  },
  confirmModalPaymentTitle: {
    color: '#E91E63',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 10,
  },
  confirmModalPaymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  confirmModalPaymentLabel: {
    color: '#ccc',
    fontSize: 13,
  },
  confirmModalPaymentValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  confirmModalPaymentNote: {
    color: '#888',
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },
  confirmModalRulesBox: {
    backgroundColor: '#0f1220',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  confirmModalRulesTitle: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 8,
  },
  confirmModalRulesText: {
    color: '#9aa0b4',
    fontSize: 12,
    lineHeight: 18,
    marginBottom: 2,
  },
  confirmModalTosBox: {
    marginBottom: 20,
  },
  confirmModalTosText: {
    color: '#888',
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 16,
  },
  confirmModalTosLink: {
    color: '#FCD000',
    textDecorationLine: 'underline',
  },
  confirmModalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmModalCancelBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#3a4060',
    alignItems: 'center',
  },
  confirmModalCancelText: {
    color: '#9aa0b4',
    fontSize: 15,
    fontWeight: '600',
  },
  confirmModalConfirmBtn: {
    flex: 2,
    padding: 14,
    borderRadius: 10,
    backgroundColor: '#FCD000',
    alignItems: 'center',
  },
  confirmModalConfirmText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '700',
  },

  // Team Info Styles
  teamInfoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  teamModalInfoBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#9146FF30',
  },
  teamModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  teamModalTitle: {
    color: '#9146FF',
    fontSize: 14,
    fontWeight: '700',
  },
  teamModalText: {
    color: '#8a8fa8',
    fontSize: 12,
    lineHeight: 18,
  },
});